import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiClient from "../utils/api";
import AnalyticsConversations from "./AnalyticsConversations";
import { useToast } from "../context/ToastContext";
import CustomDropdown from "../components/CustomDropdown";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import {
  LayoutGrid,
  MessageSquare,
  User,
  Settings,
  ChevronDown,
  ChevronUp,
  X,
  Search,
  ArrowLeft,
  AlertCircle,
  Trash2,
  Lock,
  Activity,
  FileText,
  Eye,
  Code,
  Send,
  ShieldCheck,
  Image,
  Phone,
  Mail,
  MapPin,
  Globe,
  Clock,
  Languages,
  Calendar,
  CalendarCheck,
  Hourglass,
  Plus,
  Pin,
  Edit3
} from "lucide-react";
const LANGUAGE_OPTIONS = [
  { value: "English", label: "English" },
  { value: "Tamil", label: "Tamil" },
  { value: "Spanish", label: "Spanish" },
  { value: "French", label: "French" },
  { value: "German", label: "German" },
  { value: "Mandarin", label: "Mandarin" },
  { value: "Hindi", label: "Hindi" },
  { value: "Arabic", label: "Arabic" },
  { value: "Portuguese", label: "Portuguese" },
  { value: "Russian", label: "Russian" },
  { value: "Japanese", label: "Japanese" }
];

const TAG_COLOR_PALETTES = [
  { bg: "#fdf2f8", text: "#db2777", border: "#fbcfe8" }, // Pink
  { bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0" }, // Green
  { bg: "#eff6ff", text: "#2563eb", border: "#bfdbfe" }, // Blue
  { bg: "#faf5ff", text: "#7c3aed", border: "#e9d5ff" }, // Purple
  { bg: "#fff7ed", text: "#ea580c", border: "#ffedd5" }, // Orange
  { bg: "#ecfeff", text: "#0891b2", border: "#c5f6fa" }, // Cyan
  { bg: "#f0fdfa", text: "#0d9488", border: "#ccfbf1" }, // Teal
  { bg: "#fef2f2", text: "#dc2626", border: "#fee2e2" }, // Red
  { bg: "#fefafd", text: "#c026d3", border: "#f5d0fe" }  // Fuchsia
];

const getTagStyle = (tagString) => {
  if (!tagString) return { bg: "#f1f5f9", text: "#475569", border: "#e2e8f0" };
  let hash = 0;
  for (let i = 0; i < tagString.length; i++) {
    hash = tagString.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % TAG_COLOR_PALETTES.length;
  return TAG_COLOR_PALETTES[index];
};

// Default built-in groups
const DEFAULT_GROUPS = [
  { name: "Basic information", key: "ec_basic_information", parentGroup: null },
  { name: "Telephone numbers", key: "ec_telephone_numbers", parentGroup: "ec_basic_information" }
];

const TIMEZONE_OPTIONS = [
  { value: "GMT-12:00 International Date Line West", label: "GMT-12:00 International Date Line West" },
  { value: "GMT-11:00 Midway Island, Samoa", label: "GMT-11:00 Midway Island, Samoa" },
  { value: "GMT-10:00 Hawaii", label: "GMT-10:00 Hawaii" },
  { value: "GMT-09:00 Alaska", label: "GMT-09:00 Alaska" },
  { value: "GMT-08:00 Pacific Time (US & Canada)", label: "GMT-08:00 Pacific Time (US & Canada)" },
  { value: "GMT-07:00 Mountain Time (US & Canada)", label: "GMT-07:00 Mountain Time (US & Canada)" },
  { value: "GMT-06:00 Central Time (US & Canada)", label: "GMT-06:00 Central Time (US & Canada)" },
  { value: "GMT-05:00 Eastern Time (US & Canada)", label: "GMT-05:00 Eastern Time (US & Canada)" },
  { value: "GMT-04:00 Atlantic Time (Canada)", label: "GMT-04:00 Atlantic Time (Canada)" },
  { value: "GMT-03:00 Buenos Aires, Georgetown", label: "GMT-03:00 Buenos Aires, Georgetown" },
  { value: "GMT-02:00 Mid-Atlantic", label: "GMT-02:00 Mid-Atlantic" },
  { value: "GMT-01:00 Azores, Cape Verde Islands", label: "GMT-01:00 Azores, Cape Verde Islands" },
  { value: "GMT+00:00 Western Europe Time, London", label: "GMT+00:00 Western Europe Time, London" },
  { value: "GMT+01:00 Brussels, Copenhagen, Madrid, Paris", label: "GMT+01:00 Brussels, Copenhagen, Madrid, Paris" },
  { value: "GMT+02:00 Kaliningrad, South Africa", label: "GMT+02:00 Kaliningrad, South Africa" },
  { value: "GMT+03:00 Baghdad, Riyadh, Moscow, St. Petersburg", label: "GMT+03:00 Baghdad, Riyadh, Moscow, St. Petersburg" },
  { value: "GMT+03:30 Tehran", label: "GMT+03:30 Tehran" },
  { value: "GMT+04:00 Abu Dhabi, Muscat, Baku, Tbilisi", label: "GMT+04:00 Abu Dhabi, Muscat, Baku, Tbilisi" },
  { value: "GMT+04:30 Kabul", label: "GMT+04:30 Kabul" },
  { value: "GMT+05:00 Ekaterinburg, Islamabad, Karachi, Tashkent", label: "GMT+05:00 Ekaterinburg, Islamabad, Karachi, Tashkent" },
  { value: "GMT+05:30 Chennai, Kolkata, Mumbai, New Delhi", label: "GMT+05:30 Chennai, Kolkata, Mumbai, New Delhi" },
  { value: "GMT+05:45 Kathmandu", label: "GMT+05:45 Kathmandu" },
  { value: "GMT+06:00 Almaty, Novosibirsk, Dhaka", label: "GMT+06:00 Almaty, Novosibirsk, Dhaka" },
  { value: "GMT+06:30 Yangon (Rangoon)", label: "GMT+06:30 Yangon (Rangoon)" },
  { value: "GMT+07:00 Bangkok, Hanoi, Jakarta", label: "GMT+07:00 Bangkok, Hanoi, Jakarta" },
  { value: "GMT+08:00 Beijing, Chongqing, Hong Kong, Urumqi", label: "GMT+08:00 Beijing, Chongqing, Hong Kong, Urumqi" },
  { value: "GMT+09:00 Osaka, Sapporo, Tokyo, Seoul", label: "GMT+09:00 Osaka, Sapporo, Tokyo, Seoul" },
  { value: "GMT+09:30 Adelaide, Darwin", label: "GMT+09:30 Adelaide, Darwin" },
  { value: "GMT+10:00 Canberra, Melbourne, Sydney, Vladivostok", label: "GMT+10:00 Canberra, Melbourne, Sydney, Vladivostok" },
  { value: "GMT+11:00 Magadan, Solomon Islands, New Caledonia", label: "GMT+11:00 Magadan, Solomon Islands, New Caledonia" },
  { value: "GMT+12:00 Auckland, Wellington, Fiji, Kamchatka", label: "GMT+12:00 Auckland, Wellington, Fiji, Kamchatka" }
];

// Calendar Helper Constants & Functions



const SYSTEM_FIELDS = [
  { name: "Full name", key: "name", type: "text", isSystem: true, required: true, quick_add: true, placeholder: "e.g. John Doe", tooltip: "The contact's display name", group: "ec_basic_information" },
  { name: "Email", key: "email", type: "text", isSystem: true, required: false, quick_add: true, placeholder: "e.g. john@example.com", tooltip: "Primary contact email address", group: "ec_basic_information" },
  { name: "Mobile", key: "phone", type: "text", isSystem: true, required: false, quick_add: true, placeholder: "e.g. +1 234 567 8900", tooltip: "Primary mobile phone number", group: "ec_telephone_numbers" },
  { name: "Total chat sessions", key: "total_sessions", type: "number", isSystem: true, required: false, quick_add: false, placeholder: "", tooltip: "Total number of chat sessions", group: "ec_basic_information", read_only: true },
  { name: "Created at", key: "created_at", type: "text", isSystem: true, required: false, quick_add: false, placeholder: "", tooltip: "Timestamp of first contact interaction", group: "ec_basic_information", read_only: true },
  { name: "Updated at", key: "updated_at", type: "text", isSystem: true, required: false, quick_add: false, placeholder: "", tooltip: "Timestamp of last contact interaction", group: "ec_basic_information", read_only: true },
  { name: "Language", key: "locale", type: "text", isSystem: true, required: false, quick_add: false, placeholder: "e.g. en_US", tooltip: "The region or language preference of the contact", group: "ec_basic_information" },
  { name: "Time Zone", key: "timezone", type: "text", isSystem: true, required: false, quick_add: false, placeholder: "e.g. GMT+5:30", tooltip: "Local time zone of the contact", group: "ec_basic_information" },
  { name: "Contact Age", key: "contact_age", type: "text", isSystem: true, required: false, quick_add: false, placeholder: "", tooltip: "How long this contact has been in the system since creation", group: "ec_basic_information", read_only: true }
];



const CustomDatePickerField = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);

  const initialDate = value ? new Date(value) : new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getFormattedValue = () => {
    if (!value) return "Select date...";
    const d = new Date(value);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  
  const firstDayIndex = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const prevTotalDays = new Date(year, month, 0).getDate();

  const days = [];
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    days.push({ day: prevTotalDays - i, isCurrentMonth: false, date: new Date(year, month - 1, prevTotalDays - i) });
  }
  for (let i = 1; i <= totalDays; i++) {
    days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
  }

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const handleSelectDay = (dayObj) => {
    setSelectedDate(dayObj.date);
    const yyyy = dayObj.date.getFullYear();
    const mm = String(dayObj.date.getMonth() + 1).padStart(2, '0');
    const dd = String(dayObj.date.getDate()).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}`);
    setIsOpen(false);
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  return (
    <div className="relative w-full" ref={popoverRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border border-zinc-200 rounded-xl text-xs bg-zinc-50 flex justify-between items-center transition-all cursor-pointer hover:bg-zinc-100"
      >
        <div className="flex items-center gap-2">
          
          <span className={value ? "text-zinc-900 font-semibold" : "text-zinc-400"}>{getFormattedValue()}</span>
        </div>
        <span className="text-zinc-400">▼</span>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-zinc-200 rounded-2xl shadow-xl p-4 z-[9999] w-[280px] animate-fadeIn">
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 hover:bg-zinc-50 rounded-lg text-zinc-600 transition-colors"
            >
              ◀
            </button>
            <span className="text-[11px] font-black uppercase tracking-wider text-zinc-900">
              {monthNames[month]} {year}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 hover:bg-zinc-50 rounded-lg text-zinc-600 transition-colors"
            >
              ▶
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center text-[9px] font-bold text-zinc-400 mb-2 uppercase tracking-wider">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(w => (
              <div key={w}>{w}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center">
            {days.map((dayObj, idx) => {
              const isSelected = selectedDate && 
                selectedDate.getDate() === dayObj.date.getDate() && 
                selectedDate.getMonth() === dayObj.date.getMonth() && 
                selectedDate.getFullYear() === dayObj.date.getFullYear();
              
              const isToday = new Date().toDateString() === dayObj.date.toDateString();

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelectDay(dayObj)}
                  className={`h-7 w-7 text-[10px] font-bold rounded-lg transition-all flex items-center justify-center ${
                    !dayObj.isCurrentMonth ? 'text-zinc-300' : 'text-zinc-700 hover:bg-zinc-50'
                  } ${
                    isSelected ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm' : ''
                  } ${
                    isToday && !isSelected ? 'border border-emerald-500 text-emerald-600' : ''
                  }`}
                >
                  {dayObj.day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tabs state
  const [activeTab, setActiveTab] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("tab") || "overview";
  }); // 'overview', 'conversations', 'details', 'activity', 'notes', 'proactive'

  // Proactive messages state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [whatsappConfigured, setWhatsappConfigured] = useState(null); // null=unknown, true=configured, false=not configured
  // Template variable values: { "1": "John", "2": "Acme" }
  const [templateVars, setTemplateVars] = useState({});
  const [deliveryMethod, setDeliveryMethod] = useState("now"); // 'now' or 'schedule'
  const [scheduleDateTime, setScheduleDateTime] = useState("");

  // Note state
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState("");
  const [selectedNoteColor, setSelectedNoteColor] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  // Tag state
  const [tags, setTags] = useState([]);
  const [newTagText, setNewTagText] = useState("");
  const [showAddTagInput, setShowAddTagInput] = useState(false);

  // Activities state
  const [activities, setActivities] = useState([]);

  // Conversations state
  const [activeSession, setActiveSession] = useState(null);

  // Revert Action states
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [selectedActivityToRevert, setSelectedActivityToRevert] = useState(null);
  const [isReverting, setIsReverting] = useState(false);
  const [proactiveMediaId, setProactiveMediaId] = useState("");
  const [proactiveFileName, setProactiveFileName] = useState("");
  const [proactiveFileLocalUrl, setProactiveFileLocalUrl] = useState("");

  // Contact details states
  const [detailFilter, setDetailFilter] = useState("all"); // 'all', 'basic'
  const [searchFieldQuery, setSearchFieldQuery] = useState("");
  // eslint-disable-next-line no-unused-vars
  const showEmptyFields = true; // Always show empty fields (toggle not yet implemented)



  // Manage Fields definitions
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [groups, setGroups] = useState(DEFAULT_GROUPS);

  // Accordion open/close states
  const [accordionOpen, setAccordionOpen] = useState({});

  const [isEditingAllDetails, setIsEditingAllDetails] = useState(false);
  const [tempFieldValues, setTempFieldValues] = useState({});
  const [saveStatus, setSaveStatus] = useState("");

  const [isEditingAvatar, setIsEditingAvatar] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast("File size too large. Max 5MB allowed.", "error");
      return;
    }

    try {
      const res = await ApiClient.uploadContactAvatar(contact.id, file);
      if (res && res.avatar_url) {
        const newCustomParams = {
          ...(contact.custom_parameters || {}),
          avatar_url: res.avatar_url
        };
        delete newCustomParams.avatar_seed;

        setContact(prev => ({
          ...prev,
          custom_parameters: newCustomParams
        }));
        showToast("Avatar uploaded successfully!", "success");
        setIsEditingAvatar(false);
      }
    } catch (err) {
      console.error("Error uploading avatar:", err);
      showToast("Failed to upload avatar. Please try again.", "error");
    }
  };

  const handleResetAvatar = async () => {
    try {
      const newCustomParams = {
        ...(contact.custom_parameters || {})
      };
      delete newCustomParams.avatar_url;
      const randomSeed = Math.random().toString(36).substring(2, 9);
      newCustomParams.avatar_seed = randomSeed;

      await ApiClient.updateContact(contact.id, {
        custom_parameters: {
          ...newCustomParams,
          avatar_url: null
        }
      });

      setContact(prev => ({
        ...prev,
        custom_parameters: newCustomParams
      }));
      showToast("Avatar reset to default random seed!", "success");
      setIsEditingAvatar(false);
    } catch (err) {
      console.error("Error resetting avatar:", err);
      showToast("Failed to reset avatar. Please try again.", "error");
    }
  };

  const handleUpdateFieldsClick = () => {
    const initialVals = {
      name: contact?.name || "",
      email: contact?.email === "N/A" ? "" : (contact?.email || ""),
      phone: contact?.phone === "N/A" ? "" : (contact?.phone || ""),
      locale: contact?.custom_parameters?.locale || "English",
      timezone: contact?.custom_parameters?.timezone || "GMT+05:30 Chennai, Kolkata, Mumbai, New Delhi",
      ...(contact?.custom_parameters || {})
    };
    setTempFieldValues(initialVals);
    setIsEditingAllDetails(true);
  };

  const handleSaveAllDetails = async () => {
    const standardKeys = ["name", "email", "phone"];
    const standardUpdates = {};
    const paramUpdates = {};
    const changedFieldsList = [];

    Object.keys(tempFieldValues).forEach(key => {
      // Get the old value
      let oldVal = "";
      if (standardKeys.includes(key)) {
        oldVal = contact[key] || "";
      } else {
        oldVal = contact?.custom_parameters?.[key] || "";
      }
      
      // Standardize "N/A" and empty values for comparison
      const normOld = (oldVal === "N/A" || !oldVal) ? "" : String(oldVal).trim();
      const normNew = String(tempFieldValues[key] || "").trim();

      if (normOld !== normNew) {
        if (standardKeys.includes(key)) {
          standardUpdates[key] = tempFieldValues[key];
        } else {
          paramUpdates[key] = tempFieldValues[key];
        }

        // Descriptive label for the activity log
        let fieldLabel = key;
        const matchedDef = SYSTEM_FIELDS.find(f => f.key === key) || customFieldDefinitions.find(f => f.key === key);
        if (matchedDef) {
          fieldLabel = matchedDef.name;
        }

        if (!normOld && normNew) {
          changedFieldsList.push(`added field "${fieldLabel}" as "${normNew}"`);
        } else if (normOld && !normNew) {
          changedFieldsList.push(`deleted field "${fieldLabel}"`);
        } else {
          changedFieldsList.push(`updated field "${fieldLabel}" from "${normOld}" to "${normNew}"`);
        }
      }
    });

    setSaveStatus("Saving...");
    try {
      let activityObj = null;
      if (changedFieldsList.length > 0) {
        let msg = "";
        if (changedFieldsList.length === 1) {
          msg = changedFieldsList[0];
        } else {
          msg = `modified multiple fields: ${changedFieldsList.join(", ")}`;
        }
        // Capitalize first letter
        msg = msg.charAt(0).toUpperCase() + msg.slice(1);
        activityObj = {
          id: Date.now().toString(),
          message: msg,
          timestamp: new Date().toISOString()
        };
      }

      await saveContactUpdate(standardUpdates, paramUpdates, "Fields updated successfully!", activityObj);
      setIsEditingAllDetails(false);
    } catch (err) {
      console.error("Save all details failed:", err);
    } finally {
      setSaveStatus("");
    }
  };





  useEffect(() => {
    if (id) {
      fetchContactDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (activeTab === "proactive") {
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const activeAiAgentId = localStorage.getItem("activeAiAgentId");

      // Step 1: Check WhatsApp config first — avoid hitting Meta API with invalid/missing token
      const configRes = await ApiClient.request("/whatsapp/config", { aiAgentId: activeAiAgentId });
      const config = configRes?.config;
      const isActive = config?.is_active;
      // "__UNCHANGED__" is the backend's masked placeholder meaning the token IS saved
      const hasToken = config?.access_token && config.access_token.length > 0;
      const hasWaba = config?.waba_id;

      if (!config || !isActive || !hasToken || !hasWaba) {
        setWhatsappConfigured(false);
        setTemplates([]);
        return;
      }

      setWhatsappConfigured(true);

      // Step 2: Fetch templates from Meta via backend
      const res = await ApiClient.request("/whatsapp/templates", { aiAgentId: activeAiAgentId });
      setTemplates((res.templates || []).filter(t => t.status === "APPROVED"));
    } catch (e) {
      console.error("Error loading templates:", e);
      // Only show a toast for unexpected errors — config issues already handled above
      if (!e.message?.includes("not found") && !e.message?.includes("not configured")) {
        showToast("Failed to load WhatsApp templates", "error");
      }
      setWhatsappConfigured(false);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Helper: extract all {{N}} variable indices from template components
  const extractTemplateVarIndices = (template) => {
    if (!template?.components) return [];
    const indices = new Set();
    const pattern = /\{\{(\d+)\}\}/g;
    template.components.forEach(c => {
      if ((c.type === "BODY" || c.type === "HEADER") && c.text) {
        let match;
        while ((match = pattern.exec(c.text)) !== null) {
          indices.add(match[1]);
        }
      }
    });
    const sorted = Array.from(indices).sort((a, b) => Number(a) - Number(b));
    const buttonsComp = template.components?.find(c => c.type === "BUTTONS");
    const hasDynamicUrl = buttonsComp?.buttons?.find(b => b.type === "URL" && b.url?.includes("{{1}}"));
    if (hasDynamicUrl) {
      sorted.push("button_url");
    }
    return sorted;
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplate) {
      showToast("Please select a template to send", "error");
      return;
    }
    if (!contact?.phone || contact.phone === "N/A") {
      showToast("Contact phone number is not available", "error");
      return;
    }

    // Validate variables
    const varIndices = extractTemplateVarIndices(selectedTemplate);
    const missingVars = varIndices.filter(idx => !templateVars[idx]?.trim());
    if (missingVars.length > 0) {
      showToast(`Please fill in all variable fields: ${missingVars.map(i => `{{${i}}}`).join(", ")}`, "error");
      return;
    }

    // Validate media header
    const hasMediaHeader = selectedTemplate.components?.find(c => c.type === "HEADER" && ["IMAGE", "VIDEO", "DOCUMENT"].includes(c.format));
    if (hasMediaHeader && !proactiveMediaId) {
      showToast(`Please upload a ${hasMediaHeader.format.toLowerCase()} file for the header.`, "error");
      return;
    }

    // Validate schedule
    if (deliveryMethod === "schedule") {
      if (!scheduleDateTime) {
        showToast("Please pick a date and time to schedule the message", "error");
        return;
      }
      if (new Date(scheduleDateTime).getTime() <= Date.now()) {
        showToast("Schedule time must be in the future", "error");
        return;
      }
    }

    setSendingTemplate(true);
    try {
      const activeAiAgentId = localStorage.getItem("activeAiAgentId");

      const buttonsComp = selectedTemplate.components?.find(c => c.type === "BUTTONS");
      const dynamicUrlButton = buttonsComp?.buttons?.find(b => b.type === "URL" && b.url?.includes("{{1}}"));
      const dynamicUrlIndex = buttonsComp?.buttons?.findIndex(b => b.type === "URL" && b.url?.includes("{{1}}"));

      let components = undefined;
      if (varIndices.length > 0 || hasMediaHeader || dynamicUrlButton) {
        components = [];
        
        // Add media header parameters if present
        if (hasMediaHeader && proactiveMediaId) {
          const mediaType = hasMediaHeader.format.toLowerCase();
          components.push({
            type: "header",
            parameters: [
              {
                type: mediaType,
                [mediaType]: {
                  id: proactiveMediaId
                }
              }
            ]
          });
        }

        const bodyComp = selectedTemplate.components?.find(c => c.type === "BODY");
        const headerComp = selectedTemplate.components?.find(c => c.type === "HEADER" && c.format === "TEXT");
        if (headerComp) {
          const headerVars = Array.from(new Set([...headerComp.text.matchAll(/\{\{(\d+)\}\}/g)].map(m => m[1])));
          const filteredHeaderVars = headerVars.filter(idx => idx !== "button_url");
          if (filteredHeaderVars.length > 0) {
            components.push({
              type: "header",
              parameters: filteredHeaderVars.map(idx => ({ type: "text", text: templateVars[idx] || "" }))
            });
          }
        }
        if (bodyComp) {
          const bodyVars = Array.from(new Set([...bodyComp.text.matchAll(/\{\{(\d+)\}\}/g)].map(m => m[1])));
          const filteredBodyVars = bodyVars.filter(idx => idx !== "button_url");
          if (filteredBodyVars.length > 0) {
            components.push({
              type: "body",
              parameters: filteredBodyVars.map(idx => ({ type: "text", text: templateVars[idx] || "" }))
            });
          }
        }

        if (dynamicUrlButton && templateVars["button_url"]) {
          components.push({
            type: "button",
            sub_type: "url",
            index: String(dynamicUrlIndex !== -1 ? dynamicUrlIndex : 0),
            parameters: [
              {
                type: "text",
                text: templateVars["button_url"].trim()
              }
            ]
          });
        }
      }

      const payload = {
        to: contact.phone,
        template_name: selectedTemplate.name,
        language_code: selectedTemplate.language,
        ...(components ? { components } : {})
      };

      if (deliveryMethod === "schedule") {
        payload.scheduled_time = new Date(scheduleDateTime).toISOString();
      }

      const res = await ApiClient.post("/whatsapp/templates/send", payload, { aiAgentId: activeAiAgentId });

      if (res && res.success) {
        if (res.scheduled) {
          showToast(`Template scheduled successfully for ${new Date(scheduleDateTime).toLocaleString()}! 🗓️`, "success");
        } else {
          showToast("Template sent successfully! 🎉", "success");
        }
        setSelectedTemplate(null);
        setTemplateVars({});
        setDeliveryMethod("now");
        setScheduleDateTime("");
        setProactiveMediaId("");
        setProactiveFileName("");
        setProactiveFileLocalUrl("");

        const logMessage = res.scheduled
          ? `Proactive WhatsApp Template scheduled for ${new Date(scheduleDateTime).toLocaleString()}: "${selectedTemplate.name}"`
          : `Proactive WhatsApp Template sent: "${selectedTemplate.name}"`;

        const activityObj = {
          id: Date.now().toString(),
          message: logMessage,
          timestamp: new Date().toISOString()
        };
        const currentActivities = [activityObj, ...(contact?.custom_parameters?.activities || [])].slice(0, 50);
        setActivities(currentActivities);

        setContact(prev => ({
          ...prev,
          custom_parameters: {
            ...(prev.custom_parameters || {}),
            activities: currentActivities
          }
        }));
      } else {
        showToast("Failed to send template message", "error");
      }
    } catch (err) {
      console.error("Send template failed:", err);
      showToast(err.message || "Failed to send template message", "error");
    } finally {
      setSendingTemplate(false);
    }
  };

  const fetchContactDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiClient.getContact(id);
      setContact(res);

      // Parse custom parameters
      const customParams = res.custom_parameters || {};
      setNotes(customParams.notes || []);
      setTags(customParams.tags || []);
      setActivities(customParams.activities || []);
      try {
        const fieldsRes = await ApiClient.getGlobalCustomFields();
        let rawData = fieldsRes.fields || [];
        if (rawData && !Array.isArray(rawData) && rawData.fields) {
          setCustomFieldDefinitions(rawData.fields);
          setGroups(rawData.groups || DEFAULT_GROUPS);
        } else {
          setCustomFieldDefinitions(Array.isArray(rawData) ? rawData : []);
          setGroups(DEFAULT_GROUPS);
        }
      } catch (err) {
        console.error("Error loading global custom fields:", err);
        setCustomFieldDefinitions(customParams.defined_fields || []);
        setGroups(DEFAULT_GROUPS);
      }

      // Set active session if sessions exist
      if (res.sessions && res.sessions.length > 0) {
        // Find if we already had an active session, else default to first
        const matched = res.sessions.find(s => s.session_id === activeSession?.session_id);
        setActiveSession(matched || res.sessions[0]);
      } else {
        setActiveSession(null);
      }
    } catch (err) {
      console.error("Error loading contact details:", err);
      setError("Failed to load contact details");
    } finally {
      setLoading(false);
    }
  };

  // Generic helper to update contact both locally and remotely
  const saveContactUpdate = async (updatedFields, updatedCustomParams = {}, successMessage = "Changes saved successfully!", customActivityLog = null) => {
    try {
      let currentActivities = contact?.custom_parameters?.activities || [];
      if (customActivityLog) {
        currentActivities = [customActivityLog, ...currentActivities].slice(0, 50); // Keep last 50 activities in DB
        setActivities(currentActivities);
      }

      const newCustomParams = {
        ...(contact.custom_parameters || {}),
        ...updatedCustomParams,
        activities: currentActivities
      };

      await ApiClient.updateContact(contact.id, {
        ...updatedFields,
        custom_parameters: newCustomParams
      });

      // Update local state
      setContact(prev => ({
        ...prev,
        ...updatedFields,
        custom_parameters: newCustomParams
      }));

      showToast(successMessage, "success");
    } catch (err) {
      console.error("Error saving contact update:", err);
      showToast("Failed to save changes. Please try again.", "error");
    }
  };

  // Revert selected activity log changes
  const handleRevertActivity = async () => {
    if (!selectedActivityToRevert) return;
    setIsReverting(true);

    try {
      const act = selectedActivityToRevert;
      const msg = act.message;
      let updatedFields = {};
      let updatedCustomParams = {};
      let logMsg = "";

      // 1. Tag Added Revert
      if (msg.startsWith('Tag added: "')) {
        const tagName = msg.substring('Tag added: "'.length, msg.length - 1);
        const newTags = tags.filter(t => t !== tagName);
        setTags(newTags);
        updatedCustomParams.tags = newTags;
        logMsg = `Reverted: Removed tag "${tagName}"`;
      }
      // 2. Tag Removed Revert
      else if (msg.startsWith('Tag removed: "')) {
        const tagName = msg.substring('Tag removed: "'.length, msg.length - 1);
        if (!tags.includes(tagName)) {
          const newTags = [...tags, tagName];
          setTags(newTags);
          updatedCustomParams.tags = newTags;
        }
        logMsg = `Reverted: Restored tag "${tagName}"`;
      }
      // 3. Note Added Revert (needs to look for a note with matching truncated or exact substring)
      else if (msg.startsWith('Note added: "')) {
        let noteTextSnippet = msg.substring('Note added: "'.length, msg.length - 1);
        if (noteTextSnippet.endsWith('...')) {
          noteTextSnippet = noteTextSnippet.slice(0, -3);
        }
        // Filter out the first note that matches this prefix snippet
        const indexToDel = notes.findIndex(n => n.text.startsWith(noteTextSnippet));
        let newNotes = [...notes];
        if (indexToDel !== -1) {
          newNotes.splice(indexToDel, 1);
          setNotes(newNotes);
          updatedCustomParams.notes = newNotes;
        }
        logMsg = `Reverted: Deleted note snippet "${noteTextSnippet}"`;
      }
      // 4. Note Deleted Revert (we can't easily restore the note text unless we parse the activity or note text, but we don't store note content in activity except preview. Let's do best effort)
      else if (msg.startsWith('Note deleted: "')) {
        let noteTextSnippet = msg.substring('Note deleted: "'.length, msg.length - 1);
        if (noteTextSnippet.endsWith('...')) {
          noteTextSnippet = noteTextSnippet.slice(0, -3);
        }
        const restoredNote = {
          id: Date.now().toString(),
          text: noteTextSnippet + " (Restored)",
          created_at: new Date().toISOString()
        };
        const newNotes = [restoredNote, ...notes];
        setNotes(newNotes);
        updatedCustomParams.notes = newNotes;
        logMsg = `Reverted: Restored note snippet as "${restoredNote.text}"`;
      }
      // 5. Updated/Added/Deleted Field Reverts
      else if (msg.startsWith('Updated field "') || msg.startsWith('Added field "') || msg.startsWith('Deleted field "')) {
        // Updated field "Name" from "Old" to "New"
        // Added field "Name" as "Val"
        // Deleted field "Name"
        const doubleQuoteRegex = /"([^"]+)"/g;
        const matches = [...msg.matchAll(doubleQuoteRegex)].map(m => m[1]);

        if (matches.length > 0) {
          const fieldLabel = matches[0]; // e.g. "Language" or "Mobile"
          
          // Resolve internal key from name
          const matchedDef = SYSTEM_FIELDS.find(f => f.name === fieldLabel) || customFieldDefinitions.find(f => f.name === fieldLabel);
          const fieldKey = matchedDef ? matchedDef.key : null;

          if (fieldKey) {
            const standardKeys = ["name", "email", "phone"];
            const isStandard = standardKeys.includes(fieldKey);

            if (msg.startsWith('Updated field "')) {
              // matches[1] is old value, matches[2] is new value
              const oldVal = matches[1];
              if (isStandard) {
                updatedFields[fieldKey] = oldVal;
              } else {
                updatedCustomParams[fieldKey] = oldVal;
              }
              logMsg = `Reverted: Set field "${fieldLabel}" back to "${oldVal}"`;
            } else if (msg.startsWith('Added field "')) {
              // Revert addition = delete the field value
              if (isStandard) {
                updatedFields[fieldKey] = "";
              } else {
                updatedCustomParams[fieldKey] = "";
              }
              logMsg = `Reverted: Cleared added field "${fieldLabel}"`;
            } else if (msg.startsWith('Deleted field "')) {
              // We don't store the deleted value in the log string usually unless we added custom formatting. Let's do best effort or set to empty
              if (isStandard) {
                updatedFields[fieldKey] = "";
              } else {
                updatedCustomParams[fieldKey] = "";
              }
              logMsg = `Reverted: Cleared field "${fieldLabel}"`;
            }
          }
        }
      }
      
      // If we could determine how to revert, perform the save update
      if (logMsg) {
        const activityObj = {
          id: Date.now().toString(),
          message: logMsg,
          timestamp: new Date().toISOString()
        };

        // Remove the reverted activity from list or mark it? Let's keep it but prepend the revert activity
        await saveContactUpdate(updatedFields, updatedCustomParams, "Reverted successfully!", activityObj);
      } else {
        showToast("Cannot auto-revert this type of activity.", "error");
      }
    } catch (err) {
      console.error("Revert failed:", err);
      showToast("Failed to revert. Please try again.", "error");
    } finally {
      setIsReverting(false);
      setShowRevertModal(false);
      setSelectedActivityToRevert(null);
    }
  };

  // Notes actions
  const handleAddNote = async (e, colorOverride = null) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newNoteText.trim()) return;

    const color = colorOverride || selectedNoteColor || "#FCD34D";
    const newNote = {
      id: Date.now().toString(),
      text: newNoteText,
      color,
      created_at: new Date().toISOString()
    };

    const updatedNotes = [newNote, ...notes];
    setNotes(updatedNotes);
    setNewNoteText("");
    setSelectedNoteColor(null);

    const logMsg = `Note added: "${newNoteText.substring(0, 30)}${newNoteText.length > 30 ? '...' : ''}"`;
    const activityObj = {
      id: Date.now().toString(),
      message: logMsg,
      timestamp: new Date().toISOString()
    };

    await saveContactUpdate({}, { notes: updatedNotes }, "Note added successfully!", activityObj);
  };

  const handleSaveEditedNote = async (noteId) => {
    if (!editingNoteText.trim()) return;
    const updatedNotes = notes.map(n =>
      n.id === noteId ? { ...n, text: editingNoteText } : n
    );
    setNotes(updatedNotes);
    setEditingNoteId(null);
    setEditingNoteText("");
    await saveContactUpdate({}, { notes: updatedNotes }, "Note updated!", null);
  };

  const handleDeleteNote = async (noteId) => {
    const noteToDelete = notes.find(n => n.id === noteId);
    const updatedNotes = notes.filter(n => n.id !== noteId);
    setNotes(updatedNotes);

    const logMsg = noteToDelete ? `Note deleted: "${noteToDelete.text.substring(0, 30)}${noteToDelete.text.length > 30 ? '...' : ''}"` : "Note deleted";
    const activityObj = {
      id: Date.now().toString(),
      message: logMsg,
      timestamp: new Date().toISOString()
    };

    await saveContactUpdate({}, { notes: updatedNotes }, "Note deleted successfully!", activityObj);
  };

  const handleTogglePinNote = async (noteId) => {
    const updatedNotes = notes.map(n => {
      if (n.id === noteId) {
        return { ...n, isPinned: !n.isPinned };
      }
      return n;
    });

    // Sort: Pinned notes first, then chronologically by creation date
    const sortedNotes = [...updatedNotes].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    setNotes(sortedNotes);
    
    const targetNote = notes.find(n => n.id === noteId);
    const pinStateStr = targetNote?.isPinned ? "Unpinned" : "Pinned";
    const logMsg = `${pinStateStr} note: "${targetNote?.text.substring(0, 30)}${targetNote?.text && targetNote.text.length > 30 ? '...' : ''}"`;
    const activityObj = {
      id: Date.now().toString(),
      message: logMsg,
      timestamp: new Date().toISOString()
    };

    await saveContactUpdate({}, { notes: sortedNotes }, `Note ${pinStateStr.toLowerCase()} successfully!`, activityObj);
  };

  // Tags actions
  const handleAddTag = async (e) => {
    if (e.key === "Enter" || e.type === "blur") {
      if (!newTagText.trim()) {
        setShowAddTagInput(false);
        return;
      }

      const tag = newTagText.trim();
      if (!tags.includes(tag)) {
        const updatedTags = [...tags, tag];
        setTags(updatedTags);

        const activityObj = {
          id: Date.now().toString(),
          message: `Tag added: "${tag}"`,
          timestamp: new Date().toISOString()
        };

        await saveContactUpdate({}, { tags: updatedTags }, "Tag added successfully!", activityObj);
      }
      setNewTagText("");
      setShowAddTagInput(false);
    }
  };

  const handleDeleteTag = async (tagToDelete) => {
    const updatedTags = tags.filter(t => t !== tagToDelete);
    setTags(updatedTags);

    const activityObj = {
      id: Date.now().toString(),
      message: `Tag removed: "${tagToDelete}"`,
      timestamp: new Date().toISOString()
    };

    await saveContactUpdate({}, { tags: updatedTags }, "Tag removed successfully!", activityObj);
  };



  const toggleAccordion = (sec) => {
    setAccordionOpen(prev => ({ ...prev, [sec]: !prev[sec] }));
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-pulse">
        <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-zinc-200" />
              <div className="space-y-2">
                <div className="flex items-center gap-2.5">
                  <div className="h-6 w-40 bg-zinc-200 rounded" />
                  <div className="h-4 w-16 bg-zinc-200 rounded-full" />
                </div>
                <div className="h-3 w-56 bg-zinc-200 rounded" />
              </div>
            </div>
            <div className="h-8 w-28 bg-zinc-200 rounded-lg" />
          </div>

          {/* Two Column Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 items-start">
            {/* Left Sidebar Skeleton */}
            <aside className="bg-white border border-zinc-200 rounded-xl shadow-2xs p-4 flex flex-col space-y-5">
              <div className="flex flex-col items-center text-center pb-4 border-b border-zinc-100">
                <div className="w-20 h-20 rounded-full bg-zinc-200 mb-3" />
                <div className="h-4 w-28 bg-zinc-200 rounded mb-1.5" />
                <div className="h-3 w-36 bg-zinc-200 rounded" />
              </div>

              {/* Tags Skeleton */}
              <div className="space-y-2 pb-4 border-b border-zinc-100">
                <div className="h-3 w-12 bg-zinc-200 rounded" />
                <div className="flex flex-wrap gap-1.5">
                  <div className="h-5 w-14 bg-zinc-200 rounded-full" />
                  <div className="h-5 w-18 bg-zinc-200 rounded-full" />
                </div>
              </div>

              {/* Navigation Tabs Skeleton */}
              <div className="space-y-1.5">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="h-9 w-full bg-zinc-100 rounded-lg" />
                ))}
              </div>
            </aside>

            {/* Right Main Content Skeleton */}
            <main className="min-w-0 flex-1 space-y-6">
              {/* 4 KPI Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="p-4 rounded-xl border border-zinc-200 bg-white shadow-2xs space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="h-3 w-16 bg-zinc-200 rounded" />
                      <div className="w-6 h-6 rounded bg-zinc-100" />
                    </div>
                    <div className="h-6 w-24 bg-zinc-200 rounded" />
                  </div>
                ))}
              </div>

              {/* Info Card Skeleton */}
              <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-5 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-zinc-100">
                  <div className="h-4 w-36 bg-zinc-200 rounded" />
                  <div className="h-3 w-20 bg-zinc-100 rounded" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div key={idx} className="p-3.5 rounded-xl border border-zinc-100 bg-zinc-50/50 flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-zinc-200 shrink-0" />
                      <div className="space-y-1.5 flex-1">
                        <div className="h-2.5 w-14 bg-zinc-200 rounded" />
                        <div className="h-3.5 w-28 bg-zinc-200 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] items-center justify-center p-6 text-center">
        <div className="max-w-md w-full p-8 bg-white border border-zinc-200 rounded-2xl shadow-2xs flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-600 mb-1">
            <AlertCircle size={24} />
          </div>
          <h2 className="text-base font-bold text-zinc-900 tracking-tight">Contact Not Found</h2>
          <p className="text-xs text-zinc-500 leading-relaxed">
            {error || "The requested contact record does not exist or has been deleted."}
          </p>
          <button
            onClick={() => navigate("/contacts")}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors font-semibold text-xs shadow-xs cursor-pointer"
          >
            <ArrowLeft size={14} />
            <span>Return to Contacts</span>
          </button>
        </div>
      </div>
    );
  }


  const formatWithTimezone = (dateStr, formatType = 'both') => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";

    try {
      const tzValue = isEditingAllDetails
        ? (tempFieldValues?.timezone || contact?.custom_parameters?.timezone || "")
        : (contact?.custom_parameters?.timezone || "");
      const match = tzValue.match(/GMT([+-])(\d{2}):(\d{2})/);
      let adjusted = date;
      if (match) {
        const sign = match[1] === '+' ? 1 : -1;
        const totalMinutes = sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
        adjusted = new Date(date.getTime() + totalMinutes * 60000);
      } else {
        // default to +05:30 if not specified
        adjusted = new Date(date.getTime() + 330 * 60000);
      }

      const day = String(adjusted.getUTCDate()).padStart(2, '0');
      const month = String(adjusted.getUTCMonth() + 1).padStart(2, '0');
      const year = adjusted.getUTCFullYear();
      
      let hours = adjusted.getUTCHours();
      const minutes = String(adjusted.getUTCMinutes()).padStart(2, '0');
      const seconds = String(adjusted.getUTCSeconds()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const hourStr = String(hours).padStart(2, '0');

      if (formatType === 'date') {
        return `${day}/${month}/${year}`;
      } else if (formatType === 'time') {
        return `${hourStr}:${minutes}:${seconds} ${ampm}`;
      } else {
        return `${day}/${month}/${year}, ${hourStr}:${minutes}:${seconds} ${ampm}`;
      }
    } catch (e) {
      console.error("Error formatting date with timezone:", e);
      return date.toLocaleString('en-US');
    }
  };


  const avatarSeed = contact.custom_parameters?.avatar_seed || contact.name || contact.userid || String(contact.id || 'default');

  const getAvatarUrl = () => {
    const url = contact.custom_parameters?.avatar_url;
    if (url) {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const baseDomain = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
      return `${baseDomain}${url}`;
    }
    return `https://api.dicebear.com/7.x/micah/svg?seed=${avatarSeed}&backgroundColor=b6e3f4,c0aede,d1d4f9`;
  };

  const getContactAgeString = (createdTimeStr) => {
    if (!createdTimeStr) return "N/A";
    const created = new Date(createdTimeStr);
    const now = new Date();
    const diffMs = now - created;
    if (diffMs < 0) return "Just created";
    
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffMonths / 12);
    
    if (diffYears > 0) {
      const remainingMonths = diffMonths % 12;
      return `${diffYears}y ${remainingMonths}m`;
    }
    if (diffMonths > 0) {
      const remainingDays = diffDays % 30;
      return `${diffMonths}m ${remainingDays}d`;
    }
    if (diffDays > 0) {
      const remainingHours = diffHours % 24;
      return `${diffDays}d ${remainingHours}h`;
    }
    if (diffHours > 0) {
      const remainingMins = diffMins % 60;
      return `${diffHours}h ${remainingMins}m`;
    }
    if (diffMins > 0) {
      return `${diffMins} mins`;
    }
    return "Just created";
  };

  const totalSessions = contact.sessions ? contact.sessions.length : 0;

  // Format timestamps
  let firstInitiatedTime = contact.created_at;
  let lastFinishedTime = contact.last_active;

  if (contact.sessions && contact.sessions.length > 0) {
    const earliestSession = contact.sessions[contact.sessions.length - 1];
    if (earliestSession && earliestSession.created_at) {
      firstInitiatedTime = earliestSession.created_at;
    }
    const mostRecentSession = contact.sessions[0];
    if (mostRecentSession && mostRecentSession.updated_at) {
      lastFinishedTime = mostRecentSession.updated_at;
    }
  }

  // Format timestamps dynamically using the latest contact state
  const firstSeenStr = formatWithTimezone(firstInitiatedTime);
  const lastActiveStr = formatWithTimezone(lastFinishedTime);
  const firstSeenDate = formatWithTimezone(firstInitiatedTime, 'date');
  const firstSeenTime = formatWithTimezone(firstInitiatedTime, 'time');
  const lastActiveDate = formatWithTimezone(lastFinishedTime, 'date');
  const lastActiveTime = formatWithTimezone(lastFinishedTime, 'time');




  // Determine if we should show a field based on search and show empty toggle
  const shouldShowField = (label, value) => {
    if (searchFieldQuery) {
      const matchLabel = label.toLowerCase().includes(searchFieldQuery.toLowerCase());
      const matchVal = String(value).toLowerCase().includes(searchFieldQuery.toLowerCase());
      if (!matchLabel && !matchVal) return false;
    }

    if (!showEmptyFields && !value) return false;

    return true;
  };

  const getMergedFields = () => {
    const mergedSystemFields = SYSTEM_FIELDS.map(sf => {
      const override = customFieldDefinitions.find(f => f.key === sf.key);
      if (override) {
        return { ...sf, ...override };
      }
      return sf;
    }).filter(f => !f.isDeleted);
    const customFields = customFieldDefinitions.filter(f => !SYSTEM_FIELDS.some(sf => sf.key === f.key) && !f.isDeleted);
    return [...mergedSystemFields, ...customFields];
  };

  const getFieldValue = (fieldKey) => {
    if (!contact) return "";
    if (fieldKey === "name") return contact.name || "";
    if (fieldKey === "email") return contact.email === "N/A" ? "" : (contact.email || "");
    if (fieldKey === "phone") return contact.phone === "N/A" ? "" : (contact.phone || "");

    if (fieldKey === "total_sessions") return totalSessions.toString();
    if (fieldKey === "created_at") return firstSeenStr;
    if (fieldKey === "updated_at") return lastActiveStr;

    if (fieldKey === "timezone") return contact.custom_parameters?.timezone || "GMT+05:30 Chennai, Kolkata, Mumbai, New Delhi";
    if (fieldKey === "locale") return contact.custom_parameters?.locale || "English";
    if (fieldKey === "contact_age") return getContactAgeString(firstInitiatedTime || contact.created_at);
    return contact.custom_parameters?.[fieldKey] || "";
  };

  const isCustomOrParams = (key) => !["name", "email", "phone"].includes(key);

  const getFieldGroupKey = (f) => {
    const gKey = f.group;
    if (gKey && groups.some(g => g.key === gKey)) {
      return gKey;
    }
    return null;
  };

  const fieldsData = {
    location: contact?.custom_parameters?.location || "",
    email: contact?.email === "N/A" ? "" : (contact?.email || ""),
    phone: contact?.phone === "N/A" ? "" : (contact?.phone || ""),
    work_phone: contact?.custom_parameters?.work_phone || "",
    locale: contact?.custom_parameters?.locale || "English",
    timezone: contact?.custom_parameters?.timezone || "GMT+05:30 Chennai, Kolkata, Mumbai, New Delhi",
    address: contact?.custom_parameters?.address || "",
    contact_age: getContactAgeString(firstInitiatedTime || contact.created_at),
    created_at: firstSeenStr,
    created_at_date: firstSeenDate,
    created_at_time: firstSeenTime !== "N/A" ? firstSeenTime : "",
    updated_at: lastActiveStr,
    updated_at_date: lastActiveDate,
    updated_at_time: lastActiveTime !== "N/A" ? lastActiveTime : "",
    total_sessions: totalSessions.toString()
  };

  const allFields = getMergedFields();

  const visibleGroups = detailFilter === "all"
    ? groups.filter(g => !g.parentGroup)
    : groups.filter(g => g.key === detailFilter);

  const renderFieldControl = (fd, isParam, val) => {
    const isReadOnly = !!fd.read_only;
    const placeholderText = fd.placeholder || "Click to add";

    if (isEditingAllDetails) {
      if (isReadOnly) {
        return <span className="field-value" style={{ opacity: 0.7 }}>{val || "N/A"}</span>;
      }
      
      if (fd.key === "timezone") {
        return (
          <CustomDropdown
            options={TIMEZONE_OPTIONS}
            value={tempFieldValues[fd.key] || "GMT+05:30 Chennai, Kolkata, Mumbai, New Delhi"}
            onChange={(val) => setTempFieldValues(prev => ({ ...prev, [fd.key]: val }))}
            className="w-full"
            buttonClassName="border border-zinc-200 rounded-xl"
          />
        );
      }

      if (fd.key === "locale") {
        return (
          <CustomDropdown
            options={LANGUAGE_OPTIONS}
            value={tempFieldValues[fd.key] || "English"}
            onChange={(val) => setTempFieldValues(prev => ({ ...prev, [fd.key]: val }))}
            className="w-full"
            buttonClassName="border border-zinc-200 rounded-xl"
          />
        );
      }
      
      if (fd.type === "checkbox") {
        const isChecked = tempFieldValues[fd.key] === "true" || tempFieldValues[fd.key] === true || tempFieldValues[fd.key] === "yes";
        return (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => setTempFieldValues(prev => ({ ...prev, [fd.key]: e.target.checked ? "true" : "false" }))}
            style={{ cursor: "pointer", width: "18px", height: "18px" }}
          />
        );
      }

      if (fd.type === "date") {
        return (
          <CustomDatePickerField
            value={tempFieldValues[fd.key] || ""}
            onChange={(val) => setTempFieldValues(prev => ({ ...prev, [fd.key]: val }))}
          />
        );
      }

      if (fd.type === "textarea") {
        return (
          <textarea
            className="select-input"
            style={{ minHeight: "60px", resize: "vertical", width: "100%" }}
            placeholder={placeholderText}
            value={tempFieldValues[fd.key] || ""}
            onChange={(e) => setTempFieldValues(prev => ({ ...prev, [fd.key]: e.target.value }))}
          />
        );
      }

      if (fd.type === "dropdown" || fd.type === "lookup" || fd.type === "radio" || fd.type === "multiselect") {
        let choices = fd.choices || [];
        if (fd.type === "lookup") {
          if (fd.lookupModule === "Contacts") choices = ["John Doe", "Sarah Jenkins", "Robert Smith", "Emily Davis"];
          else if (fd.lookupModule === "Companies") choices = ["Acme Corp", "Globex Corporation", "Initech", "Umbrella Corp"];
          else if (fd.lookupModule === "Deals") choices = ["100 Seats Upgrade", "Enterprise License SLA", "API Add-on contract"];
          else if (fd.lookupModule === "Users") choices = ["Sarah Jenkins (Sales Owner)", "Admin User", "Agent Support"];
          else choices = ["Lead record #104", "Partner Referral deal"];
        }
        
        const dropdownOptions = [
          { value: "", label: "-- Select Option --" },
          ...choices.map(c => ({ value: c, label: c }))
        ];

        return (
          <CustomDropdown
            options={dropdownOptions}
            value={tempFieldValues[fd.key] || ""}
            onChange={(val) => setTempFieldValues(prev => ({ ...prev, [fd.key]: val }))}
            className="w-full"
            buttonClassName="border border-zinc-200 rounded-xl"
          />
        );
      }

      return (
        <input
          type={fd.type === "number" ? "number" : "text"}
          className="select-input"
          style={{ width: "100%" }}
          placeholder={placeholderText}
          value={tempFieldValues[fd.key] || ""}
          onChange={(e) => setTempFieldValues(prev => ({ ...prev, [fd.key]: e.target.value }))}
        />
      );
    }

    // Read-only display mode when not editing
    if (val) {
      if (fd.type === "url") {
        return (
          <a
            href={val.startsWith("http") ? val : `https://${val}`}
            target="_blank"
            rel="noopener noreferrer"
            className="field-value field-value-link"
            style={{ color: "var(--grab-blue-dark)", textDecoration: "underline", wordBreak: "break-all" }}
          >
            {val}
          </a>
        );
      }

      if (fd.type === "multiselect") {
        const list = val.split(",").map(s => s.trim()).filter(s => s.length > 0);
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {list.map(item => (
              <span key={item} style={{ fontSize: "11px", fontWeight: "600", padding: "2px 6px", borderRadius: "4px", backgroundColor: "var(--grab-blue-light)", color: "var(--grab-blue-dark)" }}>
                {item}
              </span>
            ))}
          </div>
        );
      }

      if (fd.type === "checkbox") {
        const isChecked = val === "true" || val === true || val === "yes";
        return <span>{isChecked ? "yes" : "no"}</span>;
      }

      if (fd.type === "number") {
        let formattedVal = val;
        const num = parseFloat(val);
        if (!isNaN(num)) {
          if (fd.numberFormat === "percent") {
            formattedVal = `${num}%`;
          } else if (fd.numberFormat === "currency") {
            formattedVal = `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          } else if (fd.numberFormat === "decimal") {
            formattedVal = num.toLocaleString(undefined, { minimumFractionDigits: 2 });
          } else {
            formattedVal = parseInt(val).toLocaleString();
          }
        }
        return <span className="field-value">{formattedVal}</span>;
      }

      return <span className="field-value">{val}</span>;
    }

    return (
      <span className="field-value" style={{ opacity: 0.5 }}>
        N/A
      </span>
    );
  };

  const renderFieldItem = (fd) => {
    const val = getFieldValue(fd.key);
    if (!shouldShowField(fd.name, val)) return null;

    const tooltipText = fd.tooltip;
    const isParam = isCustomOrParams(fd.key);

    return (
      <div className="field-item" key={fd.key} title={tooltipText}>
        <span className="field-label" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {fd.name}
          {fd.required && <span style={{ color: "red" }}>*</span>}
          {!!fd.read_only && <span style={{ opacity: 0.5 }}><Lock size={10} /></span>}
        </span>
        {renderFieldControl(fd, isParam, val)}
      </div>
    );
  };


  const getUngroupedFields = (fieldsList) => {
    return fieldsList.filter(f => getFieldGroupKey(f) === null);
  };

  const renderOtherFieldsAccordion = (fieldsList) => {
    if (detailFilter !== "all") return null;

    const ungrouped = getUngroupedFields(fieldsList);
    if (ungrouped.length === 0) return null;

    const isExpanded = accordionOpen["other_fields"] !== false;
    const visibleFields = ungrouped.filter(f => shouldShowField(f.name, getFieldValue(f.key)));

    if (searchFieldQuery && visibleFields.length === 0) return null;
    if (!showEmptyFields && visibleFields.length === 0) return null;

    return (
      <div className="accordion-section" key="other_fields">
        <div className="accordion-header" onClick={() => toggleAccordion("other_fields")}>
          <span className="accordion-title">Other fields</span>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>

        {isExpanded && (
          <div className="accordion-content">
            {visibleFields.length > 0 ? (
              <div className="accordion-grid">
                {visibleFields.map(fd => renderFieldItem(fd))}
              </div>
            ) : (
              <div className="no-fields-message" style={{ textAlign: "center", color: "#94a3b8", padding: "20px 0", fontSize: "13.5px" }}>
                No fields found.
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (g, fieldsList) => {
    const subGroupKeys = groups.filter(sg => sg.parentGroup === g.key).map(sg => sg.key);
    const groupFields = fieldsList.filter(f => f.group === g.key || subGroupKeys.includes(f.group));
    const visibleGroupFields = groupFields.filter(f => shouldShowField(f.name, getFieldValue(f.key)));

    // Search query active but no matches in this group -> hide group
    if (searchFieldQuery && visibleGroupFields.length === 0) {
      return null;
    }
    // Show empty fields is false, and all fields are empty/invisible -> hide group
    if (!showEmptyFields && visibleGroupFields.length === 0) {
      return null;
    }

    const isExpanded = accordionOpen[g.key] !== false;

    // Group fields: direct fields first, then subgroup fields grouped by groupKey
    const chunks = [];
    
    const directFields = visibleGroupFields.filter(f => f.group === g.key);
    if (directFields.length > 0) {
      chunks.push({
        groupKey: g.key,
        isSubgroup: false,
        fields: directFields
      });
    }

    const subgroupFields = visibleGroupFields.filter(f => f.group !== g.key);
    const uniqueSubgroupKeys = [];
    subgroupFields.forEach(f => {
      if (f.group && !uniqueSubgroupKeys.includes(f.group)) {
        uniqueSubgroupKeys.push(f.group);
      }
    });

    uniqueSubgroupKeys.forEach(subgroupKey => {
      const fieldsInSubgroup = subgroupFields.filter(f => f.group === subgroupKey);
      chunks.push({
        groupKey: subgroupKey,
        isSubgroup: true,
        fields: fieldsInSubgroup
      });
    });

    return (
      <div className="accordion-section" key={g.key}>
        <div className="accordion-header" onClick={() => toggleAccordion(g.key)}>
          <span className="accordion-title">{g.name}</span>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>

        {isExpanded && (
          <div className="accordion-content" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {groupFields.length === 0 ? (
              <div className="no-fields-message" style={{ textAlign: "center", color: "#94a3b8", padding: "20px 0", fontSize: "13.5px" }}>
                No fields found.
              </div>
            ) : visibleGroupFields.length === 0 ? (
              <div className="no-fields-message" style={{ textAlign: "center", color: "#94a3b8", padding: "20px 0", fontSize: "13.5px" }}>
                No visible fields.
              </div>
            ) : (
              chunks.map((chunk, idx) => {
                if (!chunk.isSubgroup) {
                  return (
                    <div className="accordion-grid" key={`direct-${idx}`}>
                      {chunk.fields.map(fd => renderFieldItem(fd))}
                    </div>
                  );
                } else {
                  const subGroupName = groups.find(sg => sg.key === chunk.groupKey)?.name || chunk.groupKey;
                  return (
                    <div className="subgroup-wrapper-inline" key={`sub-${chunk.groupKey}-${idx}`} style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: idx > 0 ? "8px" : "0px" }}>
                      <div className="subgroup-pill-header" style={{
                        display: "inline-flex",
                        alignSelf: "flex-start",
                        backgroundColor: "#e2e8f0",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#475569",
                        border: "none"
                      }}>
                        {subGroupName}
                      </div>
                      <div className="accordion-grid" style={{ paddingLeft: 0 }}>
                        {chunk.fields.map(fd => renderFieldItem(fd))}
                      </div>
                    </div>
                  );
                }
              })
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
      <style>{`
        /* Contact Detail Dashboard CSS - EasyChat Zinc Theme Overrides */
        :root {
          --grab-blue: #18181b;
          --grab-blue-dark: #09090b;
          --grab-blue-light: #f4f4f5;
          --grab-blue-border: #e4e4e7;
          --grab-blue-bg: #ffffff;
        }

        .contact-detail-container {
          display: flex;
          flex: 1;
          background-color: transparent !important;
          color: #09090b;
          font-family: 'Inter', sans-serif !important;
          overflow: visible !important;
        }

        .contact-detail-main {
          background-color: transparent !important;
        }

        /* Left Sidebar - Profile & Tabs */
        .contact-detail-sidebar {
          width: 280px;
          background: #ffffff;
          border-right: 1px solid rgba(131, 187, 225, 0.2);
          display: flex;
          flex-direction: column;
          padding: 24px;
          flex-shrink: 0;
          overflow-y: auto;
        }

        .profile-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--grab-blue-light);
          margin-bottom: 24px;
        }

        .profile-avatar-large {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--grab-blue-dark), var(--grab-blue));
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          font-weight: 700;
          box-shadow: 0 10px 15px -3px rgba(131, 187, 225, 0.3);
          margin-bottom: 16px;
          transition: transform 0.3s ease;
        }

        .profile-avatar-large:hover {
          transform: scale(1.05);
        }

        .profile-name {
          font-size: 18px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 4px;
        }

        .profile-title {
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
          margin-bottom: 12px;
        }

        .profile-socials {
          display: flex;
          gap: 12px;
        }

        .social-icon-btn {
          color: #94a3b8;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .social-icon-btn:hover {
          color: var(--grab-blue-dark);
          background-color: var(--grab-blue-light);
        }

        .sidebar-section-title {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
          font-weight: 600;
          margin-bottom: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        /* Tabs list */
        .sidebar-tabs {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .sidebar-tab-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 8px;
          border: none;
          background: none;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          text-align: left;
          transition: all 0.2s ease;
        }

        .sidebar-tab-btn:hover {
          background-color: var(--grab-blue-light);
          color: #0f172a;
        }

        .sidebar-tab-btn.active {
          background-color: var(--grab-blue-light);
          color: var(--grab-blue-dark);
          font-weight: 600;
        }

        /* Main Panel Area */
        .contact-detail-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background-color: var(--grab-blue-bg);
        }

        .main-tab-panel {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        /* Overview Tab Styles */
        .overview-layout {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        .overview-content {
          flex: 1;
          padding: 24px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .panel-title {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
        }

        .btn-secondary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          background: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.3);
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover {
          background-color: var(--grab-blue-light);
          border-color: var(--grab-blue);
        }

        /* AI Summary card */
        .ai-summary-card {
          background: radial-gradient(circle at top left, var(--grab-blue-light) 0%, #ffffff 100%);
          border: 1px solid rgba(131, 187, 225, 0.3);
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }

        .ai-summary-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          user-select: none;
        }

        .ai-summary-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 600;
          color: var(--grab-blue-dark);
          font-size: 14px;
          margin: 0;
        }

        .ai-summary-body {
          margin-top: 12px;
          font-size: 13.5px;
          line-height: 1.6;
          color: #1e293b;
          padding-left: 28px;
        }

        /* Tags sub-bar */
        .tags-section {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .add-tag-trigger {
          font-size: 12px;
          color: #64748b;
          background: none;
          border: 1px dashed rgba(131, 187, 225, 0.5);
          border-radius: 4px;
          padding: 4px 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .add-tag-trigger:hover {
          border-color: var(--grab-blue-dark);
          color: var(--grab-blue-dark);
        }

        .tag-pill {
          font-size: 12px;
          padding: 4px 10px;
          border-radius: 100px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0px;
          transition: all 0.2s ease;
        }

        .tag-remove-btn {
          background: none;
          border: none;
          color: inherit;
          opacity: 0;
          cursor: pointer;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: opacity 0.2s ease, width 0.2s ease, margin 0.2s ease;
          width: 0;
          overflow: hidden;
        }

        .tag-pill:hover .tag-remove-btn {
          opacity: 0.75;
          width: 12px;
          margin-left: 6px;
        }

        .tag-pill:hover .tag-remove-btn:hover {
          opacity: 1;
        }

        /* Fields grid layout */
        .fields-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 20px;
          background: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.2);
          border-radius: 12px;
          padding: 24px;
        }

        .field-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 8px;
          border-radius: 6px;
          transition: background-color 0.2s ease;
        }

        .field-item:hover {
          background-color: var(--grab-blue-light);
        }

        .field-label {
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
        }

        .field-value {
          font-size: 14px;
          color: #0f172a;
          font-weight: 600;
          white-space: normal;
          overflow: visible;
          word-break: break-word;
        }

        .field-value-empty {
          color: #94a3b8;
          font-size: 13px;
          font-weight: 400;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .field-value-empty:hover {
          color: var(--grab-blue-dark);
          text-decoration: underline;
        }

        .field-value-link {
          color: var(--grab-blue-dark);
          text-decoration: none;
        }

        .field-value-link:hover {
          text-decoration: underline;
        }

        /* Notes Right Sidebar */
        .notes-sidebar {
          width: 320px;
          background: #ffffff;
          border-left: 1px solid rgba(131, 187, 225, 0.2);
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 20px;
          flex-shrink: 0;
          overflow-y: auto;
        }

        .notes-header {
          font-size: 16px;
          font-weight: 600;
          color: #0f172a;
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0;
        }

        .note-input-container {
          display: flex;
          flex-direction: column;
          gap: 10px;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 8px;
          padding: 12px;
        }

        .note-textarea {
          width: 100%;
          height: 80px;
          background: transparent;
          border: none;
          resize: none;
          font-size: 13px;
          line-height: 1.5;
          color: #78350f;
          outline: none;
        }

        .note-actions {
          display: flex;
          justify-content: flex-end;
        }

        .btn-primary-sm {
          background: #111827;
          color: #ffffff;
          border: none;
          border-radius: 4px;
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-primary-sm:hover {
          background: #1f2937;
        }

        .notes-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .note-card {
          background: #fafaf9;
          border: 1px solid #e7e5e4;
          border-radius: 8px;
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: relative;
          transition: transform 0.2s ease;
        }

        .note-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }

        .note-text {
          font-size: 13px;
          color: #44403c;
          line-height: 1.5;
          white-space: pre-wrap;
        }

        .note-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
          color: #a8a29e;
        }

        .note-delete-btn {
          background: none;
          border: none;
          color: #a8a29e;
          cursor: pointer;
          padding: 2px;
          border-radius: 4px;
        }

        .note-delete-btn:hover {
          color: #ef4444;
          background-color: #fee2e2;
        }

        /* Contact Details Tab CSS */
        .contact-details-layout {
          flex: 1;
          padding: 24px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .details-sub-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid rgba(131, 187, 225, 0.2);
          padding-bottom: 12px;
          margin-bottom: 8px;
        }

        .sub-header-filters {
          display: flex;
          gap: 8px;
        }

        .filter-pill {
          padding: 6px 12px;
          border-radius: 100px;
          border: 1px solid rgba(131, 187, 225, 0.2);
          background: #ffffff;
          color: #64748b;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filter-pill:hover {
          border-color: var(--grab-blue);
          color: #475569;
        }

        .filter-pill.active {
          background-color: var(--grab-blue-dark);
          color: #ffffff;
          border-color: var(--grab-blue-dark);
        }

        .sub-header-actions {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .details-search-wrapper {
          position: relative;
          width: 240px;
        }

        .details-search-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          pointer-events: none;
        }

        .details-search-input {
          width: 100%;
          padding: 6px 10px 6px 32px;
          border: 1px solid rgba(131, 187, 225, 0.2);
          border-radius: 6px;
          font-size: 13px;
          outline: none;
          background-color: #ffffff;
          transition: border-color 0.2s ease;
        }

        .details-search-input:focus {
          border-color: var(--grab-blue);
        }

        .toggle-empty-fields {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #64748b;
          cursor: pointer;
          user-select: none;
        }

        .toggle-switch {
          position: relative;
          width: 36px;
          height: 20px;
          background-color: #cbd5e1;
          border-radius: 100px;
          transition: background-color 0.2s ease;
        }

        .toggle-switch-handle {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background-color: #ffffff;
          transition: transform 0.2s ease;
        }

        .toggle-empty-fields input {
          display: none;
        }

        .toggle-empty-fields input:checked + .toggle-switch {
          background-color: var(--grab-blue-dark);
        }

        .toggle-empty-fields input:checked + .toggle-switch .toggle-switch-handle {
          transform: translateX(16px);
        }

        /* Accordion sections */
        .accordion-sections {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .accordion-section {
          border: 1px solid rgba(131, 187, 225, 0.2);
          border-radius: 8px;
          background: #ffffff;
          overflow: visible;
        }

        .accordion-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 20px;
          background-color: #fafafa;
          cursor: pointer;
          user-select: none;
          border-bottom: 1px solid var(--grab-blue-light);
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }

        .accordion-title {
          font-size: 14px;
          font-weight: 600;
          color: #334155;
        }

        .accordion-content {
          padding: 20px;
        }

        .accordion-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 16px;
        }

        /* Subgroup styling */
        .subgroup-wrapper {
          margin-top: 16px;
          border-top: 1px dashed rgba(131, 187, 225, 0.2);
          padding-top: 16px;
        }

        .subgroup-header {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background-color: #fafafa;
          border-left: 3px solid var(--grab-blue-dark);
          border-radius: 4px;
          margin-bottom: 12px;
          font-size: 13px;
          font-weight: 600;
          color: #475569;
        }

        .subgroup-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 16px;
          padding-left: 12px;
        }

        /* Conversations Tab CSS */
        .conversations-layout {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        .sessions-sidebar {
          width: 300px;
          border-right: 1px solid rgba(131, 187, 225, 0.2);
          background: #ffffff;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          overflow-y: auto;
        }

        .sessions-sidebar-header {
          padding: 16px 20px;
          border-bottom: 1px solid rgba(131, 187, 225, 0.2);
          font-weight: 600;
          color: #0f172a;
          font-size: 15px;
        }

        .session-item-card {
          padding: 16px 20px;
          border-bottom: 1px solid #f1f5f9;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }

        .session-item-card:hover {
          background-color: var(--grab-blue-light);
        }

        .session-item-card.active {
          background-color: var(--grab-blue-light);
          border-left: 3px solid var(--grab-blue-dark);
        }

        .session-card-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }

        .session-card-id {
          font-size: 13px;
          font-weight: 600;
          color: var(--grab-blue-dark);
          font-family: monospace;
        }

        .session-card-time {
          font-size: 11px;
          color: #94a3b8;
        }

        .session-card-summary {
          font-size: 12px;
          color: #64748b;
          line-height: 1.4;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .conversation-chat-pane {
          flex: 1;
          display: flex;
          flex-direction: column;
          background-color: var(--grab-blue-bg);
          position: relative;
          overflow: hidden;
        }

        .chat-pane-header {
          background: #ffffff;
          padding: 16px 24px;
          border-bottom: 1px solid rgba(131, 187, 225, 0.2);
          display: flex;
          flex-direction: column;
        }

        .chat-pane-title {
          font-weight: 600;
          font-size: 14px;
          color: #0f172a;
        }

        .chat-pane-subtitle {
          font-size: 11px;
          color: #94a3b8;
          margin-top: 2px;
        }

        .chat-messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .chat-message-bubble {
          display: flex;
          flex-direction: column;
          max-width: 70%;
          padding: 12px 16px;
          border-radius: 12px;
          font-size: 13.5px;
          line-height: 1.5;
          position: relative;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .chat-message-bubble.incoming {
          align-self: flex-start;
          background: #ffffff;
          color: #0f172a;
          border-bottom-left-radius: 2px;
          border: 1px solid rgba(131, 187, 225, 0.1);
        }

        .chat-message-bubble.outgoing {
          align-self: flex-end;
          background: var(--grab-blue-dark);
          color: #ffffff;
          border-bottom-right-radius: 2px;
        }

        .chat-message-time {
          align-self: flex-end;
          font-size: 10px;
          color: #94a3b8;
          margin-top: 4px;
        }

        .chat-message-bubble.outgoing .chat-message-time {
          color: rgba(255, 255, 255, 0.7);
        }

        .empty-chat-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex: 1;
          padding: 40px;
          text-align: center;
          color: #94a3b8;
          background-color: #ffffff;
        }

        .empty-chat-icon {
          margin-bottom: 16px;
          color: #cbd5e1;
        }

        .empty-chat-title {
          font-weight: 600;
          color: #475569;
          font-size: 16px;
          margin-bottom: 6px;
        }

        .empty-chat-text {
          font-size: 13px;
          max-width: 320px;
        }

        /* Modals styling */
        .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #111827;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-primary:hover {
          background: #1f2937;
        }

        /* Manage Fields Modal Styles */
        .manage-fields-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: 250px;
          overflow-y: auto;
          padding: 4px;
          margin-bottom: 20px;
        }

        .manage-field-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          background-color: var(--grab-blue-bg);
          border: 1px solid rgba(131, 187, 225, 0.2);
          border-radius: 6px;
        }

        .manage-field-row-left {
          display: flex;
          flex-direction: column;
        }

        .manage-field-name {
          font-size: 13.5px;
          font-weight: 600;
          color: #0f172a;
        }

        .manage-field-type {
          font-size: 11px;
          color: #64748b;
          margin-top: 2px;
        }

        .manage-field-delete-btn {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
        }

        .manage-field-delete-btn:hover {
          color: #ef4444;
          background-color: #fee2e2;
        }

        .add-field-form {
          display: flex;
          gap: 10px;
          align-items: flex-end;
          border-top: 1px solid rgba(131, 187, 225, 0.2);
          padding-top: 16px;
        }

        .form-flex-1 {
          flex: 1;
        }

        .select-input {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid rgba(131, 187, 225, 0.2);
          border-radius: 6px;
          font-size: 13px;
          outline: none;
          background-color: #ffffff;
        }

        .select-input:focus {
          border-color: var(--grab-blue);
        }

        .tag-editor-input {
          border: 1px solid rgba(131, 187, 225, 0.2);
          border-radius: 4px;
          padding: 2px 6px;
          font-size: 12px;
          outline: none;
          width: 100px;
        }

        .tag-editor-input:focus {
          border-color: var(--grab-blue);
        }

        /* Edit value input style */
        .inline-edit-input {
          width: 100%;
          padding: 4px 6px;
          border: 1px solid var(--grab-blue);
          border-radius: 4px;
          font-size: 13.5px;
          outline: none;
          font-weight: 600;
          color: #0f172a;
        }

        /* Manage Fields Premium UI */
        .manage-fields-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .manage-fields-list-wrapper {
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-height: 380px;
          overflow-y: auto;
          padding-right: 4px;
        }

        .field-def-row {
          background: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.25);
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.2s ease;
        }

        .field-def-row:hover {
          border-color: var(--grab-blue);
          box-shadow: 0 4px 10px rgba(131, 187, 225, 0.08);
        }

        .field-def-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          cursor: pointer;
          user-select: none;
        }

        .field-def-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .field-def-grip {
          color: #94a3b8;
          cursor: grab;
        }

        .field-def-icon {
          color: var(--grab-blue-dark);
          background: var(--grab-blue-light);
          padding: 6px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .field-def-name-wrapper {
          display: flex;
          flex-direction: column;
        }

        .field-def-name {
          font-size: 14px;
          font-weight: 600;
          color: #0f172a;
        }

        .field-def-key {
          font-size: 11px;
          color: #64748b;
          font-family: monospace;
        }

        .field-def-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .field-def-badges {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .badge-opt {
          font-size: 10px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 100px;
          text-transform: uppercase;
        }

        .badge-opt.required {
          background-color: #fee2e2;
          color: #ef4444;
        }

        .badge-opt.readonly {
          background-color: #f1f5f9;
          color: #64748b;
        }

        .badge-opt.quickadd {
          background-color: var(--grab-blue-light);
          color: var(--grab-blue-dark);
        }

        .badge-opt.unique {
          background-color: #fef3c7;
          color: #d97706;
        }

        .field-def-details {
          padding: 16px;
          background-color: var(--grab-blue-bg);
          border-top: 1px solid rgba(131, 187, 225, 0.15);
          display: flex;
          flex-direction: column;
          gap: 12px;
          animation: fadeIn 0.2s ease-out;
        }

        .field-def-detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .field-def-detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .field-def-detail-label {
          font-size: 11px;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
        }

        .field-def-detail-value {
          font-size: 13px;
          color: #334155;
        }

        .field-def-detail-value.empty {
          color: #94a3b8;
          font-style: italic;
        }

        .field-def-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          border-top: 1px dashed rgba(131, 187, 225, 0.2);
          padding-top: 12px;
        }

        /* Form styling */
        .field-form-title {
          font-size: 16px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 16px;
        }

        .form-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        /* Switch toggles */
        .options-toggles-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-top: 8px;
          margin-bottom: 20px;
        }

        .switch-toggle-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.15);
          border-radius: 8px;
          padding: 10px 14px;
          cursor: pointer;
          user-select: none;
          transition: all 0.2s ease;
        }

        .switch-toggle-item:hover {
          border-color: var(--grab-blue);
          background-color: var(--grab-blue-bg);
        }

        .switch-label-wrapper {
          display: flex;
          flex-direction: column;
        }

        .switch-title {
          font-size: 13px;
          font-weight: 600;
          color: #334155;
        }

        .switch-desc {
          font-size: 11px;
          color: #64748b;
        }

        /* The Switch slider */
        .toggle-switch-ios {
          position: relative;
          width: 40px;
          height: 22px;
          background-color: #cbd5e1;
          border-radius: 100px;
          transition: background-color 0.2s ease;
          flex-shrink: 0;
        }

        .toggle-switch-ios::after {
          content: "";
          position: absolute;
          top: 2px;
          left: 2px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background-color: #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
          transition: transform 0.2s ease;
        }

        input:checked + .toggle-switch-ios {
          background-color: var(--grab-blue-dark);
        }

        input:checked + .toggle-switch-ios::after {
          transform: translateX(18px);
        }

        /* Type selector buttons */
        .type-selector-wrapper {
          display: flex;
          gap: 10px;
          margin-top: 6px;
        }

        .type-select-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px;
          border: 1px solid rgba(131, 187, 225, 0.25);
          background: #ffffff;
          border-radius: 8px;
          font-size: 13.5px;
          font-weight: 600;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .type-select-btn.active {
          border-color: var(--grab-blue-dark);
          background: var(--grab-blue-light);
          color: var(--grab-blue-dark);
          box-shadow: 0 0 0 3px rgba(93, 169, 217, 0.15);
        }

        /* Custom Date Picker Popup Styling */
        .calendar-popover {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 6px;
          background-color: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.25);
          border-radius: 8px;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
          padding: 16px;
          z-index: 99999;
          width: 280px;
        }
        
        .calendar-header-btn {
          border: none;
          background: none;
          cursor: pointer;
          color: #64748b;
          font-size: 14px;
          padding: 6px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
        }
        
        .calendar-header-btn:hover {
          background-color: #f1f5f9;
          color: #0f172a;
        }
        
        .calendar-select {
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 13px;
          font-weight: 500;
          background: #ffffff;
          color: #334155;
          outline: none;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        
        .calendar-select:focus {
          border-color: var(--grab-blue);
        }
        
        .calendar-day-btn {
          border: none;
          background: none;
          border-radius: 6px;
          padding: 8px 0;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .calendar-day-btn.current-month {
          color: #0f172a;
        }
        
        .calendar-day-btn.other-month {
          color: #cbd5e1;
        }
        
        .calendar-day-btn:hover {
          background-color: #f1f5f9;
        }
        
        .calendar-day-btn.selected {
          border: 1px solid var(--grab-blue-dark) !important;
          background-color: var(--grab-blue-light) !important;
          color: var(--grab-blue-dark) !important;
          font-weight: 600 !important;
        }

        /* New Overview Header Card */
        .overview-header-card {
          background: radial-gradient(circle at top left, var(--grab-blue-light) 0%, #ffffff 100%);
          border: 1px solid rgba(131, 187, 225, 0.25);
          border-radius: 20px;
          padding: 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-shadow: 0 4px 20px -2px rgba(131, 187, 225, 0.1);
        }

        .overview-header-profile {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .overview-header-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background-color: var(--grab-blue);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
        }

        .overview-header-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .overview-header-name-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .overview-header-name {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .overview-header-title {
          font-size: 13.5px;
          color: #475569;
          font-weight: 500;
          margin: 0;
        }

        /* 6 Boxes Layout Grid */
        .overview-boxes-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(280px, 1fr));
          gap: 24px;
          margin-top: 24px;
        }

        @media (max-width: 1024px) {
          .overview-boxes-grid {
            grid-template-columns: repeat(2, minmax(280px, 1fr));
          }
        }

        @media (max-width: 640px) {
          .overview-boxes-grid {
            grid-template-columns: minmax(280px, 1fr);
          }
        }

        .overview-box-card {
          background: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.18);
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
          display: flex;
          flex-direction: column;
          gap: 16px;
          transition: all 0.2s ease;
        }

        .overview-box-card:hover {
          box-shadow: 0 8px 24px rgba(131, 187, 225, 0.08);
          border-color: rgba(131, 187, 225, 0.35);
        }

        .box-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 700;
          padding-bottom: 8px;
          border-bottom: 1px dashed rgba(131, 187, 225, 0.15);
        }

        .box-item-row {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .box-item-label {
          font-size: 11px;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .box-item-value {
          font-size: 13.5px;
          color: #1e293b;
          font-weight: 600;
          word-break: break-all;
        }

        /* Wide Notes Card Styling */
        .notes-wide-card {
          grid-column: span 3;
          background: #ffffff;
          border: 1px solid rgba(131, 187, 225, 0.18);
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
          margin-top: 24px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        @media (max-width: 1024px) {
          .notes-wide-card {
            grid-column: span 2;
          }
        }

        @media (max-width: 640px) {
          .notes-wide-card {
            grid-column: span 1;
          }
        }
      `}</style>
      {/* Top Header / Breadcrumb */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/contacts")}
            className="p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
            title="Back to Contacts"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
                {contact.name || "Guest User"}
              </h1>
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold border bg-zinc-100 text-zinc-700 border-zinc-200">
                {fieldsData.channel || "Contact"}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              {fieldsData.email || fieldsData.phone || "Inspect and manage customer profile, channels, and conversation sessions."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={() => setActiveTab("details")}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] cursor-pointer"
          >
            <Settings size={13} />
            <span>Edit Details</span>
          </button>
        </div>
      </div>

      {/* Two Column Layout: Left Navigation + Right Main Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 items-start">
        {/* Left Sidebar Card */}
        <aside className="bg-white border border-zinc-200 rounded-xl shadow-2xs p-4 flex flex-col space-y-5">
          {/* Profile Card */}
          <div className="flex flex-col items-center text-center pb-4 border-b border-zinc-100">
            <div 
              className="relative group cursor-pointer overflow-hidden rounded-full w-20 h-20 border-2 border-zinc-100 shadow-2xs"
              onClick={() => setIsEditingAvatar(true)}
            >
              <img
                src={getAvatarUrl()}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <span className="text-[10px] text-white font-bold uppercase tracking-wider">Edit</span>
              </div>
            </div>
            <h2 className="text-sm font-bold text-zinc-900 mt-3">{contact.name || "Guest User"}</h2>
            <span className="text-[11px] text-zinc-400 font-medium truncate max-w-[200px]">
              {fieldsData.email || fieldsData.phone || "No direct contact info"}
            </span>

            {/* Photo Upload Popup if editing */}
            {isEditingAvatar && (
              <div className="mt-3 w-full flex flex-col gap-2 p-3 bg-zinc-50 rounded-xl border border-zinc-200 animate-in fade-in duration-200 text-left">
                <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider">Upload Photo</span>
                <label className="w-full py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-center text-xs font-semibold transition-all shadow-xs cursor-pointer">
                  Choose Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                <div className="flex gap-1.5 justify-end mt-1">
                  {contact.custom_parameters?.avatar_url && (
                    <button
                      onClick={handleResetAvatar}
                      className="px-2 py-0.5 bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 rounded text-[10px] font-semibold transition-colors mr-auto"
                      title="Reset to Default"
                    >
                      Reset
                    </button>
                  )}
                  <button
                    onClick={() => setIsEditingAvatar(false)}
                    className="px-2 py-0.5 bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-600 rounded text-[10px] font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Tags Section */}
            <div className="flex items-center gap-1.5 flex-wrap justify-center mt-3 w-full">
              <button
                onClick={() => setShowAddTagInput(true)}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-md text-[10px] font-semibold transition-colors cursor-pointer"
              >
                <span>+ Tag</span>
              </button>
              {tags.map((tag) => {
                const tagStyle = getTagStyle(tag);
                return (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border"
                    style={{
                      backgroundColor: tagStyle.bg,
                      borderColor: tagStyle.border,
                      color: tagStyle.text,
                    }}
                  >
                    {tag}
                    <button onClick={() => handleDeleteTag(tag)} className="hover:opacity-75 cursor-pointer">
                      <X size={10} />
                    </button>
                  </span>
                );
              })}
              {showAddTagInput && (
                <input
                  type="text"
                  placeholder="Add tag..."
                  autoFocus
                  value={newTagText}
                  onChange={(e) => setNewTagText(e.target.value)}
                  onKeyDown={handleAddTag}
                  onBlur={handleAddTag}
                  className="px-2 py-0.5 text-[11px] bg-white border border-zinc-300 rounded-md outline-none w-20 focus:ring-1 focus:ring-zinc-900"
                />
              )}
            </div>
          </div>

          {/* Vertical Navigation Tabs */}
          <div className="flex flex-col space-y-1">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider px-2.5 pb-1">
              Navigation
            </span>
            {[
              { id: "overview", label: "Overview", icon: LayoutGrid },
              { id: "conversations", label: "Conversations", icon: MessageSquare },
              { id: "details", label: "Contact details", icon: User },
              { id: "activity", label: "Activity", icon: Activity },
              { id: "notes", label: "Notes", icon: FileText },
              { id: "proactive", label: "Proactive message", icon: Send },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    isActive
                      ? "bg-zinc-900 text-white shadow-xs"
                      : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                  }`}
                >
                  <Icon size={15} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Right Main Content Area */}
        <main className="min-w-0 flex-1 space-y-6">
          {activeTab === "overview" && (
            <div className="space-y-6 animate-slideIn">
              {/* KPI Metrics Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {/* Total Sessions */}
                <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors flex flex-col justify-between">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider truncate">Sessions</span>
                    <MessageSquare className="text-zinc-500 shrink-0" size={18} />
                  </div>
                  <div className="mt-3">
                    <div className="text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight">{fieldsData.total_sessions || 0} Chats</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 truncate">Total conversations</div>
                  </div>
                </div>

                {/* Contact Age */}
                <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors flex flex-col justify-between">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider truncate">Contact Age</span>
                    <Hourglass className="text-zinc-500 shrink-0" size={18} />
                  </div>
                  <div className="mt-3">
                    <div className="text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight">{fieldsData.contact_age || "N/A"}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 truncate">Since first created</div>
                  </div>
                </div>

                {/* Created At */}
                <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors flex flex-col justify-between" title={fieldsData.created_at}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider truncate">Created At</span>
                    <Calendar className="text-zinc-500 shrink-0" size={18} />
                  </div>
                  <div className="mt-3">
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <span className="text-sm sm:text-base font-bold text-zinc-900 tracking-tight">
                        {fieldsData.created_at_date || "N/A"}
                      </span>
                      {fieldsData.created_at_time && (
                        <span className="text-xs font-semibold text-zinc-500">
                          {fieldsData.created_at_time}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 truncate">Registration date</div>
                  </div>
                </div>

                {/* Updated At */}
                <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors flex flex-col justify-between" title={fieldsData.updated_at}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider truncate">Updated At</span>
                    <CalendarCheck className="text-zinc-500 shrink-0" size={18} />
                  </div>
                  <div className="mt-3">
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <span className="text-sm sm:text-base font-bold text-zinc-900 tracking-tight">
                        {fieldsData.updated_at_date || "N/A"}
                      </span>
                      {fieldsData.updated_at_time && (
                        <span className="text-xs font-semibold text-zinc-500">
                          {fieldsData.updated_at_time}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 truncate">Last record update</div>
                  </div>
                </div>
              </div>

              {/* Contact Information Details Card */}
              <div className="bg-white border border-zinc-200 rounded-xl shadow-2xs overflow-hidden">
                <div className="p-4 border-b border-zinc-100 bg-zinc-50/50 flex items-center justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wider">Contact Information</h3>
                    <p className="text-[11px] text-zinc-400 font-medium mt-0.5">Communication channels and demographic data</p>
                  </div>
                  <button
                    onClick={() => setActiveTab("details")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-900 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors shadow-2xs cursor-pointer"
                  >
                    <Settings size={13} />
                    <span>Manage</span>
                  </button>
                </div>

                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="p-3.5 rounded-xl border border-zinc-100 bg-zinc-50/30 flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg border border-zinc-200 text-zinc-500 shadow-2xs">
                      <Phone size={15} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Mobile</span>
                      <span className="text-xs font-semibold text-zinc-900 truncate block mt-0.5">{fieldsData.phone || "N/A"}</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl border border-zinc-100 bg-zinc-50/30 flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg border border-zinc-200 text-zinc-500 shadow-2xs">
                      <Mail size={15} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Email</span>
                      <span className="text-xs font-semibold text-zinc-900 truncate block mt-0.5" title={fieldsData.email}>{fieldsData.email || "N/A"}</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl border border-zinc-100 bg-zinc-50/30 flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg border border-zinc-200 text-zinc-500 shadow-2xs">
                      <MapPin size={15} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">City</span>
                      <span className="text-xs font-semibold text-zinc-900 truncate block mt-0.5">{fieldsData.location || "N/A"}</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl border border-zinc-100 bg-zinc-50/30 flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg border border-zinc-200 text-zinc-500 shadow-2xs">
                      <Globe size={15} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Country</span>
                      <span className="text-xs font-semibold text-zinc-900 truncate block mt-0.5">{fieldsData.address || "N/A"}</span>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl border border-zinc-100 bg-zinc-50/30 flex items-start gap-3" title={fieldsData.timezone}>
                    <div className="p-2 bg-white rounded-lg border border-zinc-200 text-zinc-500 shadow-2xs shrink-0">
                      <Clock size={15} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Time Zone</span>
                      {(() => {
                        const tz = fieldsData.timezone || "N/A";
                        const match = tz.match(/^(GMT[+-]\d{2}:\d{2})\s*(.*)$/);
                        if (match) {
                          return (
                            <div className="mt-0.5">
                              <span className="text-xs font-semibold text-zinc-900 block leading-tight">
                                {match[1]}
                              </span>
                              {match[2] && (
                                <span className="text-[11px] font-medium text-zinc-500 block leading-snug mt-0.5 break-words">
                                  {match[2]}
                                </span>
                              )}
                            </div>
                          );
                        }
                        return (
                          <span className="text-xs font-semibold text-zinc-900 block mt-0.5 break-words">
                            {tz}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl border border-zinc-100 bg-zinc-50/30 flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg border border-zinc-200 text-zinc-500 shadow-2xs">
                      <Languages size={15} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Language</span>
                      <span className="text-xs font-semibold text-zinc-900 truncate block mt-0.5">{fieldsData.locale || "English"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {activeTab === "conversations" && (
          <div className="space-y-6 animate-slideIn">
            <AnalyticsConversations
              activeAiAgentId={ApiClient.currentAiAgentId || "default"}
              contactSessionIds={contact?.sessions?.map(s => s.session_id) || []}
              isContactDetail={true}
            />
          </div>
        )}

        {activeTab === "details" && (
          <div className="space-y-6 animate-slideIn">
            <div className="contact-details-layout">
              <div className="panel-header" style={{ marginBottom: "8px" }}>
                <h1 className="panel-title">Contact details</h1>
                <div className="flex gap-2">
                  {isEditingAllDetails ? (
                    <button className="btn-secondary" style={{ gap: "6px" }} onClick={() => setIsEditingAllDetails(false)}>
                      <span>Cancel</span>
                    </button>
                  ) : (
                    <button className="btn-primary" style={{ gap: "6px" }} onClick={handleUpdateFieldsClick}>
                      <span>Update Fields</span>
                    </button>
                  )}
                  <button className="btn-primary" style={{ gap: "6px" }} onClick={() => navigate(`/contacts/fields?contact_ref=${contact.id}`)}>
                    <Settings size={14} />
                    <span>Manage fields</span>
                  </button>
                </div>
              </div>

              {/* Sub header filters */}
              <div className="details-sub-header">
                <div className="sub-header-filters">
                  <button
                    className={`filter-pill ${detailFilter === "all" ? "active" : ""}`}
                    onClick={() => setDetailFilter("all")}
                  >
                    All details
                  </button>
                  {groups.filter(g => !g.parentGroup).map(g => (
                    <button
                      key={g.key}
                      className={`filter-pill ${detailFilter === g.key ? "active" : ""}`}
                      onClick={() => setDetailFilter(g.key)}
                    >
                      {g.name}
                    </button>
                  ))}
                </div>

                <div className="sub-header-actions">
                  <div className="details-search-wrapper">
                    <Search size={14} className="details-search-icon" />
                    <input
                      type="text"
                      placeholder="Search fields"
                      className="details-search-input"
                      value={searchFieldQuery}
                      onChange={(e) => setSearchFieldQuery(e.target.value)}
                    />
                  </div>

              
                </div>
              </div>

              <div className="accordion-sections">
                {visibleGroups.map(g => renderGroup(g, allFields))}
                {renderOtherFieldsAccordion(allFields)}
              </div>

              {isEditingAllDetails && (
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end", gap: "12px", borderTop: "1px solid rgba(131, 187, 225, 0.2)", paddingTop: "16px" }}>
                  <button 
                    className="btn-primary" 
                    onClick={handleSaveAllDetails} 
                    disabled={!!saveStatus}
                    style={{ padding: "10px 24px", fontSize: "14px", fontWeight: "700" }}
                  >
                    {saveStatus || "Save Changes"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "activity" && (
          <div className="space-y-6 animate-slideIn">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight flex items-center gap-2">
                  <Activity size={18} className="text-zinc-700" />
                  <span>Activity Log</span>
                </h1>
                <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
                  Track history of changes and lifecycle events for this contact.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-zinc-200 shadow-2xs p-6 flex flex-col gap-4">
              <div className="flex justify-between items-center pb-2 border-b border-zinc-100">
                <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider font-semibold">Recent Activities</h3>
              </div>

                {(!activities || activities.length === 0) ? (
                  <div className="p-12 border border-dashed border-slate-200 rounded-2xl text-center text-xs text-slate-400">
                    No contact activities recorded yet.
                  </div>
                ) : (
                  <div className="relative pl-8 flex flex-col gap-6 mt-4">
                    {/* Timeline connecting vertical line */}
                    <div className="absolute left-[3px] top-3 bottom-3 w-0.5 bg-slate-200"></div>

                    {activities.slice(0, 15).map((act, index) => {
                      const dateStr = formatWithTimezone(act.timestamp);
                      const isActive = index === 0;
                      return (
                        <div key={act.id || index} className="relative flex flex-col gap-1.5 animate-fadeIn">
                          {/* Connector dot */}
                          <div className={`absolute -left-[32px] top-2 w-2.5 h-2.5 rounded-full border-2 ${
                            isActive ? "bg-blue-500 border-blue-500 ring-4 ring-blue-50" : "bg-slate-300 border-white"
                          }`}></div>
                          
                          <div 
                            onClick={() => {
                              if (act.message === "New chat is created") {
                                return; // Do nothing
                              }
                              if (!act.message.startsWith("Reverted: ")) {
                                setSelectedActivityToRevert(act);
                                setShowRevertModal(true);
                              } else {
                                showToast("This is a revert action log and cannot be reverted again.", "info");
                              }
                            }}
                            className={`flex items-center justify-between p-3.5 rounded-2xl transition-all group border border-transparent ${
                              act.message === "New chat is created" 
                                ? "cursor-default hover:bg-slate-50/30" 
                                : "cursor-pointer hover:bg-white hover:shadow-sm hover:border-slate-150"
                            }`}
                            title={
                              act.message !== "New chat is created" && !act.message.startsWith("Reverted: ") 
                                ? "Click to Revert Activity" 
                                : undefined
                            }
                          >
                            <span className="text-[13.5px] font-semibold text-slate-800 leading-snug group-hover:text-blue-600 transition-colors">
                              {act.message}
                            </span>
                            <span className="text-[11px] font-semibold text-slate-400 whitespace-nowrap ml-4 flex items-center gap-2">
                              {dateStr}
                              {!act.message.startsWith("Reverted: ") && act.message !== "New chat is created" && (
                                <span className="text-[9px] font-bold text-blue-500 uppercase bg-blue-50 px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-all transform translate-x-1 group-hover:translate-x-0 whitespace-nowrap">
                                  Revert
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
        )}

        {activeTab === "notes" && (() => {
          const NOTE_COLORS = [
            { id: "yellow",  hex: "#FCD34D" },
            { id: "orange",  hex: "#FB923C" },
            { id: "purple",  hex: "#C084FC" },
            { id: "cyan",    hex: "#22D3EE" },
            { id: "lime",    hex: "#A3E635" },
            { id: "pink",    hex: "#F472B6" },
            { id: "teal",    hex: "#2DD4BF" },
          ];

          const formatNoteDate = (dateStr) => {
            if (!dateStr) return "";
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            const tzValue = contact?.custom_parameters?.timezone || "";
            const match = tzValue.match(/GMT([+-])(\d{2}):(\d{2})/);
            let adjusted = d;
            if (match) {
              const sign = match[1] === '+' ? 1 : -1;
              const totalMins = sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
              adjusted = new Date(d.getTime() + (totalMins - (-d.getTimezoneOffset())) * 60000);
            }
            const day = String(adjusted.getUTCDate()).padStart(2, '0');
            const month = String(adjusted.getUTCMonth() + 1).padStart(2, '0');
            const year = adjusted.getUTCFullYear();
            const timePart = adjusted.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
            return `${day}/${month}/${year}, ${timePart}`;
          };

          return (
            <div className="space-y-6 animate-slideIn">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Internal Notes</h1>
                  <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
                    Manage team notes, follow-up records, and context for this contact.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (selectedNoteColor) {
                        setSelectedNoteColor(null);
                        setNewNoteText("");
                      } else {
                        setSelectedNoteColor(NOTE_COLORS[0].hex);
                      }
                    }}
                    className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] cursor-pointer"
                  >
                    <Plus size={14} />
                    <span>{selectedNoteColor ? "Cancel" : "Add Note"}</span>
                  </button>
                </div>
              </div>

              {/* Note Creation Card (active when adding a note) */}
              {selectedNoteColor && (
                <div className="bg-white rounded-xl border border-zinc-200 shadow-2xs p-5 space-y-4 animate-fadeIn">
                  <div className="flex items-center justify-between gap-3 pb-3 border-b border-zinc-100">
                    <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">Note Theme</span>
                    <div className="flex items-center gap-2">
                      {NOTE_COLORS.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setSelectedNoteColor(c.hex)}
                          className={`w-5 h-5 rounded-full transition-transform cursor-pointer ${
                            selectedNoteColor === c.hex ? 'ring-2 ring-zinc-900 ring-offset-2 scale-110' : 'hover:scale-110 opacity-80 hover:opacity-100'
                          }`}
                          style={{ backgroundColor: c.hex }}
                          title={c.id}
                        />
                      ))}
                    </div>
                  </div>

                  <textarea
                    autoFocus
                    value={newNoteText}
                    onChange={e => setNewNoteText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAddNote(null, selectedNoteColor);
                      if (e.key === "Escape") { setSelectedNoteColor(null); setNewNoteText(""); }
                    }}
                    placeholder="Type note content... (Ctrl+Enter to save, Esc to cancel)"
                    className="w-full min-h-[100px] p-3 text-xs sm:text-[13px] font-medium text-zinc-800 bg-zinc-50/50 border border-zinc-200 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-all resize-y"
                  />

                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => { setSelectedNoteColor(null); setNewNoteText(""); }}
                      className="px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleAddNote(null, selectedNoteColor)}
                      disabled={!newNoteText.trim()}
                      className="px-4 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-50 disabled:pointer-events-none transition-all font-semibold text-xs shadow-xs cursor-pointer"
                    >
                      Save Note
                    </button>
                  </div>
                </div>
              )}

              {/* Notes Grid or Empty State */}
              {(!notes || notes.length === 0) && !selectedNoteColor ? (
                <div className="bg-white rounded-xl border border-zinc-200 shadow-2xs p-16 text-center flex flex-col items-center justify-center">
                  <div className="w-12 h-12 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 mb-3 shadow-2xs">
                    <FileText size={22} />
                  </div>
                  <h3 className="text-sm font-bold text-zinc-800 tracking-tight">No notes recorded</h3>
                  <p className="text-xs text-zinc-500 max-w-sm mt-1 mb-5">
                    Add internal notes, customer preferences, or key account context.
                  </p>
                  <button
                    onClick={() => setSelectedNoteColor(NOTE_COLORS[0].hex)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] cursor-pointer"
                  >
                    <Plus size={14} />
                    <span>Add First Note</span>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {notes.map((note) => {
                    const cardColor = note.color || "#FCD34D";
                    const isEditing = editingNoteId === note.id;

                    return (
                      <div
                        key={note.id}
                        className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-4 flex flex-col justify-between hover:border-zinc-300 transition-all group relative overflow-hidden"
                        style={{ borderTop: `3px solid ${cardColor}` }}
                      >
                        {/* Header: Date + Pin & Actions */}
                        <div className="flex items-center justify-between pb-2 border-b border-zinc-100">
                          <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                            {formatNoteDate(note.created_at)}
                          </span>

                          <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleTogglePinNote(note.id)}
                              className={`p-1 rounded-md transition-colors cursor-pointer ${
                                note.isPinned ? 'text-amber-500 bg-amber-50' : 'text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100'
                              }`}
                              title={note.isPinned ? "Unpin note" : "Pin note"}
                            >
                              <Pin size={13} className={note.isPinned ? "fill-current" : ""} />
                            </button>
                            <button
                              onClick={() => { setEditingNoteId(note.id); setEditingNoteText(note.text); }}
                              className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors cursor-pointer"
                              title="Edit note"
                            >
                              <Edit3 size={13} />
                            </button>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              className="p-1 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                              title="Delete note"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="my-3 flex-1 min-h-[60px]">
                          {isEditing ? (
                            <div className="space-y-2">
                              <textarea
                                autoFocus
                                value={editingNoteText}
                                onChange={e => setEditingNoteText(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSaveEditedNote(note.id);
                                  if (e.key === "Escape") { setEditingNoteId(null); setEditingNoteText(""); }
                                }}
                                className="w-full min-h-[80px] p-2 text-xs font-medium text-zinc-800 bg-zinc-50 border border-zinc-200 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 resize-none"
                              />
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={() => { setEditingNoteId(null); setEditingNoteText(""); }}
                                  className="px-2 py-1 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-100 rounded transition-colors cursor-pointer"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleSaveEditedNote(note.id)}
                                  className="px-2.5 py-1 text-[11px] font-semibold bg-zinc-900 text-white rounded hover:bg-zinc-800 transition-colors shadow-2xs cursor-pointer"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs sm:text-[13px] font-medium text-zinc-800 leading-relaxed whitespace-pre-wrap break-words">
                              {note.text}
                            </p>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-2 border-t border-zinc-100 text-[10px] text-zinc-400 mt-auto">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cardColor }} />
                            <span className="font-medium text-zinc-500">Internal Note</span>
                          </div>
                          {note.isPinned && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-bold border border-amber-200/60 text-[9px] uppercase tracking-wider">
                              Pinned
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
        {activeTab === "proactive" && (
          <div className="space-y-6 animate-slideIn">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight flex items-center gap-2">
                  <Send size={18} className="text-zinc-700" />
                  <span>Proactive Message</span>
                </h1>
                <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
                  Select an approved WhatsApp template and dispatch it to this contact.
                </p>
              </div>
            </div>
            <div className="w-full space-y-6">

              {templatesLoading ? (
                <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-16 flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 rounded-full border-2 border-zinc-200 border-t-zinc-900 animate-spin mb-3" />
                  <h3 className="text-sm font-bold text-zinc-900 tracking-tight">Loading templates...</h3>
                  <p className="text-xs text-zinc-500 mt-1">
                    Fetching approved WhatsApp message templates for this agent.
                  </p>
                </div>
              ) : whatsappConfigured === false ? (
                <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-12 text-center flex flex-col items-center justify-center">
                  <div className="w-12 h-12 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-500 mb-3 shadow-2xs">
                    <MessageSquare size={22} />
                  </div>
                  <h3 className="text-sm font-bold text-zinc-900 tracking-tight">WhatsApp Not Configured</h3>
                  <p className="text-xs text-zinc-500 max-w-sm mt-1 mb-5">
                    To send proactive messages, connect and activate your WhatsApp integration first.
                  </p>
                  <button
                    onClick={() => navigate('/whatsapp-config')}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] cursor-pointer"
                  >
                    <span>Go to WhatsApp Settings</span>
                  </button>
                </div>
              ) : (!templates || templates.length === 0) ? (
                <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-16 text-center flex flex-col items-center justify-center">
                  <div className="w-12 h-12 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 mb-3 shadow-2xs">
                    <FileText size={22} />
                  </div>
                  <h3 className="text-sm font-bold text-zinc-900 tracking-tight">No Templates Available</h3>
                  <p className="text-xs text-zinc-500 max-w-sm mt-1">
                    No approved WhatsApp templates found for this agent. Create templates in WhatsApp Manager first.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                  
                  {/* Left Column */}
                  <div className="space-y-6">
                    
                    {/* Panel 1: Select Template */}
                    <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-5 space-y-4">
                      <div className="flex items-center justify-between pb-3 border-b border-zinc-100">
                        <div className="flex items-center gap-2">
                          <FileText size={15} className="text-zinc-700" />
                          <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Select Template</h3>
                        </div>
                        <span className="text-[11px] text-zinc-400 font-medium">{templates.length} available</span>
                      </div>
                      
                      <div className="max-h-[260px] overflow-y-auto border border-zinc-200/80 rounded-xl">
                        <table className="w-full text-left border-collapse">
                          <thead className="sticky top-0 bg-zinc-50/95 backdrop-blur-xs border-b border-zinc-200 z-10">
                            <tr>
                              <th className="py-2.5 px-3 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Template Name</th>
                              <th className="py-2.5 px-3 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Category</th>
                              <th className="py-2.5 px-3 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Language</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-100">
                            {templates.map(t => {
                              const isSelected = selectedTemplate?.id === t.id;
                              return (
                                <tr
                                  key={t.id || t.name}
                                  onClick={() => { setSelectedTemplate(t); setTemplateVars({}); }}
                                  className={`transition-colors cursor-pointer ${
                                    isSelected ? "bg-zinc-50 font-semibold" : "hover:bg-zinc-50/50"
                                  }`}
                                >
                                  <td className="py-2.5 px-3 text-xs text-zinc-900 flex items-center gap-2.5">
                                    <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 ${
                                      isSelected ? "border-4 border-zinc-900 bg-white" : "border-zinc-300"
                                    }`} />
                                    <span className="truncate max-w-[160px]">{t.name}</span>
                                  </td>
                                  <td className="py-2.5 px-3 text-xs text-zinc-600">
                                    {t.category ? t.category.charAt(0) + t.category.slice(1).toLowerCase() : "General"}
                                  </td>
                                  <td className="py-2.5 px-3">
                                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold border bg-zinc-100 text-zinc-700 border-zinc-200">
                                      {t.language}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Panel: Media Header Upload */}
                    {selectedTemplate && selectedTemplate.components?.some(c => c.type === "HEADER" && ["IMAGE", "VIDEO", "DOCUMENT"].includes(c.format)) && (() => {
                      const hasMediaHeader = selectedTemplate.components.find(c => c.type === "HEADER");
                      const mediaFormat = hasMediaHeader.format;
                      return (
                        <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-5 space-y-4">
                          <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
                            <Image size={15} className="text-zinc-700" />
                            <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                              Header {mediaFormat.charAt(0) + mediaFormat.slice(1).toLowerCase()}
                            </h3>
                          </div>
                          <input
                            type="file"
                            accept={mediaFormat === "IMAGE" ? "image/png, image/jpeg" : mediaFormat === "VIDEO" ? "video/mp4" : "application/pdf"}
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              
                              setProactiveFileLocalUrl(URL.createObjectURL(file));
                              setProactiveFileName(file.name);

                              const formData = new FormData();
                              formData.append("file", file);
                              
                              showToast("Uploading media asset to Meta...", "info");
                              try {
                                const res = await ApiClient.request("/whatsapp/campaigns/upload-media", {
                                  method: "POST",
                                  body: formData
                                });
                                if (res && res.media_id) {
                                  setProactiveMediaId(res.media_id);
                                  showToast("Media uploaded successfully!", "success");
                                }
                              } catch (err) {
                                showToast("Failed to upload media: " + (err.message || "Upload failed"), "error");
                              }
                            }}
                            className="hidden"
                            id="proactive-media-upload"
                          />

                          {!proactiveMediaId ? (
                            <div 
                              className="border-2 border-dashed border-zinc-200 hover:border-zinc-400 bg-zinc-50/50 rounded-xl p-5 text-center cursor-pointer transition-all"
                              onClick={() => document.getElementById("proactive-media-upload").click()}
                            >
                              <div className="text-xs text-zinc-600 font-medium">Click to upload header {mediaFormat.toLowerCase()}</div>
                              <div className="text-[11px] text-zinc-400 mt-1">PNG, JPG, MP4 or PDF</div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                              <div className="flex items-center gap-2.5 min-w-0">
                                {proactiveFileLocalUrl && mediaFormat === "IMAGE" && (
                                  <img 
                                    src={proactiveFileLocalUrl} 
                                    alt="Thumbnail" 
                                    className="w-9 h-9 object-cover rounded" 
                                  />
                                )}
                                {proactiveFileLocalUrl && mediaFormat === "VIDEO" && (
                                  <video 
                                    src={proactiveFileLocalUrl} 
                                    className="w-9 h-9 object-cover rounded" 
                                  />
                                )}
                                {mediaFormat === "DOCUMENT" && (
                                  <div className="w-9 h-9 bg-zinc-200 rounded flex items-center justify-center text-[10px] font-bold text-zinc-700">
                                    PDF
                                  </div>
                                )}
                                <span className="text-xs font-semibold text-zinc-900 truncate">
                                  {proactiveFileName || "proactive_file"}
                                </span>
                              </div>
                              <button 
                                onClick={() => {
                                  setProactiveMediaId("");
                                  setProactiveFileName("");
                                  setProactiveFileLocalUrl("");
                                }}
                                className="p-1 text-zinc-400 hover:text-rose-600 rounded transition-colors cursor-pointer"
                                title="Remove file"
                              >
                                <X size={15} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Panel 2: Fill in Template Variables */}
                    {selectedTemplate && (() => {
                      const varIndices = extractTemplateVarIndices(selectedTemplate);
                      if (varIndices.length === 0) return null;
                      return (
                        <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-5 space-y-4">
                          <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
                            <Code size={15} className="text-zinc-700" />
                            <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Template Variables</h3>
                          </div>
                          <div className="space-y-3">
                            {varIndices.map(idx => (
                              <div key={idx} className="flex items-center gap-2.5">
                                <span className="px-2.5 py-1 bg-zinc-100 border border-zinc-200 rounded-lg text-xs font-mono font-bold text-zinc-700 shrink-0">
                                  {`{{${idx}}}`}
                                </span>
                                <input
                                  type="text"
                                  placeholder={`Enter value for {{${idx}}}...`}
                                  value={templateVars[idx] || ""}
                                  onChange={e => setTemplateVars(prev => ({ ...prev, [idx]: e.target.value }))}
                                  className="flex-1 px-3 py-1.5 text-xs font-medium text-zinc-900 bg-white border border-zinc-200 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-all"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Bottom Action Bar */}
                    {selectedTemplate && (
                      <div className="pt-2">
                        <button
                          onClick={handleSendTemplate}
                          disabled={sendingTemplate}
                          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-50 disabled:pointer-events-none transition-all font-semibold text-xs shadow-xs active:scale-[0.98] cursor-pointer"
                        >
                          <span>{sendingTemplate ? "Sending..." : "Send Template"}</span>
                          <Send size={13} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Right Column: Message Preview Panel */}
                  {selectedTemplate ? (() => {
                    const substituteVars = (text) => {
                      return text.replace(/\{\{(\d+)\}\}/g, (_, idx) => {
                        const val = templateVars[idx]?.trim();
                        return val ? val : `{{${idx}}}`;
                      });
                    };

                    return (
                      <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-5 space-y-4">
                        <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
                          <Eye size={15} className="text-zinc-700" />
                          <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">Message Preview</h3>
                        </div>

                        {/* Simulated WhatsApp Device */}
                        <div className="bg-[#efeae2] rounded-xl p-4 sm:p-5 flex flex-col justify-start min-h-[260px] border border-zinc-200/60 shadow-inner">
                          <div className="bg-white rounded-xl p-3.5 max-w-[90%] self-start shadow-xs relative text-xs text-zinc-900 space-y-2">
                            {/* Header element if any */}
                            {selectedTemplate.components?.map((c, idx) => {
                              if (c.type === "HEADER") {
                                if (c.format === "TEXT") {
                                  return (
                                    <div key={idx} className="font-bold text-zinc-900 leading-tight">
                                      {substituteVars(c.text)}
                                    </div>
                                  );
                                } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(c.format)) {
                                  return (
                                    <div key={idx} className="w-full h-32 bg-zinc-100 rounded-lg flex items-center justify-center overflow-hidden">
                                      {proactiveFileLocalUrl ? (
                                        c.format === "IMAGE" ? (
                                          <img src={proactiveFileLocalUrl} alt="Preview" className="w-full h-full object-cover" />
                                        ) : c.format === "VIDEO" ? (
                                          <video src={proactiveFileLocalUrl} controls autoPlay muted className="w-full h-full object-cover" />
                                        ) : (
                                          <div className="p-2 text-xs font-bold text-zinc-600">PDF Document</div>
                                        )
                                      ) : (
                                        <span className="text-[11px] text-zinc-400">[Header {c.format.toLowerCase()} preview]</span>
                                      )}
                                    </div>
                                  );
                                }
                              }
                              return null;
                            })}

                            {/* Body element */}
                            {selectedTemplate.components?.map((c, idx) => {
                              if (c.type === "BODY") {
                                return (
                                  <div key={idx} className="whitespace-pre-wrap leading-relaxed text-zinc-800 text-xs">
                                    {substituteVars(c.text)}
                                  </div>
                                );
                              }
                              return null;
                            })}

                            {/* Footer element */}
                            {selectedTemplate.components?.map((c, idx) => {
                              if (c.type === "FOOTER") {
                                return (
                                  <div key={idx} className="text-[10px] text-zinc-400">
                                    {c.text}
                                  </div>
                                );
                              }
                              return null;
                            })}

                            {/* Buttons element */}
                            {selectedTemplate.components?.map((c, idx) => {
                              if (c.type === "BUTTONS" && c.buttons) {
                                return (
                                  <div key={idx} className="border-t border-zinc-100 pt-2 -mx-3.5 -mb-3.5 divide-y divide-zinc-100 flex flex-col">
                                    {c.buttons.map((btn, bidx) => (
                                      <div key={bidx} className="py-2 px-3 text-center text-xs font-semibold text-emerald-600">
                                        <span>{btn.text}</span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                              return null;
                            })}

                            {/* Simulated WhatsApp ticks and time */}
                            <div className="flex items-center justify-end gap-1 text-[9px] text-zinc-400 pt-1">
                              <span>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              <span className="text-sky-500 font-bold">✓✓</span>
                            </div>
                          </div>
                        </div>

                        {/* Footer verification tag */}
                        <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200/60 rounded-lg text-emerald-800 text-[11px] font-medium">
                          <ShieldCheck size={14} className="shrink-0 text-emerald-600" />
                          <span>WhatsApp message preview with variable substitution.</span>
                        </div>
                      </div>
                    );
                  })() : (
                    <div className="bg-white rounded-xl border border-zinc-200/80 shadow-2xs p-12 text-center flex flex-col items-center justify-center text-zinc-400">
                      <Eye size={24} className="opacity-30 mb-2" />
                      <p className="text-xs font-medium text-zinc-600">No template selected</p>
                      <p className="text-[11px] text-zinc-400 mt-0.5">Select a template from the list to preview message.</p>
                    </div>
                  )}

                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  </div>

      {/* Revert Activity Confirmation Popup */}
      <DeleteConfirmModal
        isOpen={showRevertModal}
        onClose={() => {
          setShowRevertModal(false);
          setSelectedActivityToRevert(null);
        }}
        onConfirm={handleRevertActivity}
        title="Revert Change"
        message={`Are you sure you want to revert this activity? "${selectedActivityToRevert?.message}"`}
        confirmText="Revert Now"
        cancelText="Cancel"
        isLoading={isReverting}
      />
    </div>
  );
}
